package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.AppearanceAwareImage
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Constants
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.util.Kotlin

@Page("/compose/clean_architecture")
@Composable
fun ComposeCleanArchitecturePage() {
    BlogLayout(
        PageProperties(
            "MVVM, Clean Architecture",
            "Clean Architecture aims to provide a clear separation of concerns, making the application easier to develop, test, and maintain."
        ),
        BlogProperties("Jetpack Compose / Kotlin Multiplatform", "Basic, MVVM, Clean Architecture")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            WebImage(
                src = Resources.Images.CLEAN_ARCHITECTURE_SOLID,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "clean architecture solid principal"
            )

            Ul {
                Li {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Single Responsibility",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A class should have one reason to change. Separate ViewModel, Repository, and DataSource.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        class WeatherViewModel(
                            private val apiService: WeatherApiService
                        ) : ViewModel() {
                            
                            // ui responsibility
                            private val _state = MutableStateFlow(WeatherState())
                            val state = _state.asStateFlow()    
                            fun fetchWeather(city: String) {
                                viewModelScope.launch {
                                    _state.update { it.copy(isLoading = true) }     
                                    try {
                                        // networking responsibility (should NOT be here)
                                        val response = apiService.getWeather(BuildConfig.API_KEY, city)
                                        _state.update { it.copy(weatherResult = Result.Success(response)) }
                                    } catch (e: Exception) {
                                        // error mapping responsibility (should NOT be here)
                                        val error = if (e is IOException) DataError.Network.NO_INTERNET else DataError.Network.UNKNOWN
                                        _state.update { it.copy(weatherResult = Result.Error(error)) }
                                    }
                                }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        // Repository only handles Data & Mapping
                        class WeatherRepositoryImpl(
                            private val apiService: WeatherApiService
                        ) : WeatherRepository {
                            override suspend fun getWeatherData(city: String): Result<WeatherResponse, DataError.Network> {
                                return try {
                                    val response = apiService.getWeather(BuildConfig.WEATHER_API_KEY, city)
                                    Result.Success(response)
                                } catch (e: Exception) {
                                    val networkError = when (e) {
                                        is HttpException -> when (e.code()) {
                                            400 -> DataError.Network.CITY_NOT_FOUND
                                            else -> DataError.Network.UNKNOWN
                                        }
                                        is IOException -> DataError.Network.NO_INTERNET
                                        else -> DataError.Network.UNKNOWN
                                    }
                                    Result.Error(networkError)
                                }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        // ViewModel only handles UI State
                        class WeatherViewModel(
                            private val repository: WeatherRepository
                        ) : ViewModel() {
                            
                            private val _state = MutableStateFlow(WeatherState())
                            val state = _state.asStateFlow()

                            fun onAction(action: WeatherAction) {
                                if (action is WeatherAction.OnSearchClick) {
                                    fetchWeather(_state.value.searchQuery)
                                }
                            }

                            private fun fetchWeather(city: String) {
                                viewModelScope.launch {
                                    _state.update { it.copy(weatherResult = Result.Loading) }
                                    // just delegates the work to the specialist (Repository)
                                    val result = repository.getWeatherData(city)
                                    _state.update { it.copy(weatherResult = result) }
                                }
                            }
                        }
                    """.trimIndent()
                )

                Li {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Open/Closed",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Classes should be open for extension, closed for modification. Use interfaces & sealed classes in Kotlin",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        // The repository logic is tightly coupled to specific HTTP codes.
                        // If we add a new API service, we have to copy-paste or modify this logic.
                        override suspend fun getWeatherData(city: String): Result<WeatherResponse, DataError.Network> {
                            return try {
                                val response = weatherApiService.getWeather(BuildConfig.WEATHER_API_KEY, city)
                                Result.Success(response)
                            } catch (e: Exception) {
                                val networkError = when (e) {
                                    is HttpException -> {
                                        // Hardcoded logic: If we need to change how 400 is handled,
                                        // we MUST modify this specific function.
                                        if (e.code() == 400) DataError.Network.CITY_NOT_FOUND
                                        else DataError.Network.UNKNOWN
                                    }
                                    else -> DataError.Network.UNKNOWN
                                }
                                Result.Error(networkError)
                            }
                        }

                    """.trimIndent()
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In this example, every time a new error type or logic is added, we must modify the core repository function. This is “closed” to extension because we keep changing the same lines of code.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In better code practice we move the error mapping logic into a separate extension function. This allows us to extend the functionality without modifying the core logic of WeatherRepositoryImpl.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        // The repository remains simple. 
                        // It uses an extension to "resolve" the error.
                        override suspend fun getWeatherData(city: String): Result<WeatherResponse, DataError.Network> {
                            return try {
                                val response = weatherApiService.getWeather(BuildConfig.WEATHER_API_KEY, city)
                                Result.Success(response)
                            } catch (e: Exception) {
                                // We extend the Exception class with a mapper
                                Result.Error(e.toDataError()) 
                            }
                        }
                    """.trimIndent()
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This code is clean and will never change, even if you add 10 new HTTP error codes. (Closed for Modification)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        // Mapper.kt (Utility file)
                        fun Exception.toDataError(): DataError.Network {
                            return when (this) {
                                is HttpException -> when (this.code()) {
                                    400 -> DataError.Network.CITY_NOT_FOUND
                                    401 -> DataError.Network.UNAUTHORISED
                                    429 -> DataError.Network.TOO_MANY_REQUESTS
                                    else -> DataError.Network.UNKNOWN
                                }
                                is IOException -> DataError.Network.NO_INTERNET
                                else -> DataError.Network.UNKNOWN
                            }
                        }
                    """.trimIndent()
                )

                Li {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Liskov Substitution",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Objects of a superclass should be replaceable with objects of its subclasses without breaking its behavior. Subclasses should replace parent classes without breaking behaviour. Avoid forcing child classes to override unnecessary methods.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        //The contract
                        interface WeatherRepository {
                            suspend fun getWeatherData(city: String): Result<WeatherResponse, DataError.Network>
                        }
                        
                        //This follows the contract perfectly.
                        class WeatherRepositoryImpl(
                            private val weatherApiService: WeatherApiService
                        ): WeatherRepository {
                            override suspend fun getWeatherData(city: String): Result<WeatherResponse, DataError.Network> {
                                return try {
                                    val response = weatherApiService.getWeather(BuildConfig.WEATHER_API_KEY, city)
                                    Result.Success(response)
                                } catch (e: Exception) {
                                    // Even in failure, we return a Result.Error, staying true to the interface
                                    Result.Error(e.toDataError())
                                }
                            }
                        }
                    """.trimIndent()
                )

                Li {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Interface Segregation",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Many small interfaces are better than one big interface. Cleaner contracts = fewer unused functions.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        //That you shouldn’t be forced to implement a function that you don’t need.
                        
                        interface ApiService {
                            @GET("weather/current.json")
                            suspend fun getWeather(@Query("q") city: String): WeatherResponse

                            @GET("news/latest")
                            suspend fun getNews(@Query("lang") lang: String): NewsResponse

                            @GET("stocks/quote")
                            suspend fun getStockPrice(@Query("symbol") sym: String): StockResponse
                        }

                        // The WeatherRepository is now forced to depend on an interface that
                        // contains News and Stock logic it doesn't need.
                        class WeatherRepositoryImpl(private val apiService: ApiService) : WeatherRepository {
                            // ...
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        // Segregated Interfaces
                        // Specific interfaces for specific features
                        interface WeatherApiService {
                            @GET("current.json")
                            suspend fun getWeather(
                                @Query("key") apikey: String,
                                @Query("q") city: String
                            ): WeatherResponse
                        }

                        interface NewsApiService {
                            @GET("news/latest")
                            suspend fun getNews(
                                Query("lang") lang: String
                            ): NewsResponse
                        }
                        
                        // Specific Implementation
                        // Now, the Weather feature only depends on what it actually uses.
                        class WeatherRepositoryImpl(
                            private val weatherApiService: WeatherApiService
                        ): WeatherRepository {
                            override suspend fun getWeatherData(city: String): Result<WeatherResponse, DataError.Network> {
                                // ... logic only for weather
                            }
                        }
                    """.trimIndent()
                )

                Li {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Dependency Inversion",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Depend on abstractions, not implementations. Interfaces + constructor injection.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                        // High-level module depends on a low-level detail
                        class WeatherViewModel() : ViewModel() {
                            
                            // ViewModel is "creating" its own dependency.
                            // It is tightly coupled to WeatherRepositoryImpl.
                            private val repository = WeatherRepositoryImpl(RetrofitInstance.weatherApi)

                            fun loadWeather(city: String) {
                                viewModelScope.launch {
                                    repository.getWeatherData(city)
                                }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        // The abstraction (interface)
                        interface WeatherRepository {
                            suspend fun getWeatherData(city: String): Result<WeatherResponse, DataError.Network>
                        }
                        
                        // High-Level Module (viewmodel)
                        // ViewModel depends on an Interface (Abstraction)
                        class WeatherViewModel(
                            private val repository: WeatherRepository // Injected via constructor
                        ) : ViewModel() {
                            
                            fun onAction(action: WeatherAction) {
                                // ViewModel interacts with the abstraction
                                viewModelScope.launch {
                                    repository.getWeatherData("Tokyo")
                                }
                            }
                        }
                        
                        // Dependency Injector (Koin Module)
                        val weatherModule = module {
                            // We bind the implementation to the interface
                            singleOf(::WeatherRepositoryImpl) { bind<WeatherRepository>() }
                            
                            viewModelOf(::WeatherViewModel)
                        }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "Monolithic Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In a monolithic design, all components are tightly coupled into a single codebase. This makes development quick at the beginning because everything is in one place. However, as the app grows, the lack of separation makes it harder to modify or scale individual parts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Explain modularization strategies",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Faster builds (changed module + dependents only)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Enforced boundaries (feature A can't access feature B's internal classes)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Independent testing per module",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Team ownership per module",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //By Feature:  :feature:home, :feature:cart, :feature:profile
                    //By Layer:    :core:data, :core:domain, :core:ui, :core:network
                    //Hybrid:      Feature modules depend on core modules
                    """.trimIndent()
            )

            SpanText(
                text = "Abstraction",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Abstraction allows you to define a contract or blueprint without worrying about the underlying implementation. In Kotlin, abstraction is commonly achieved using:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                        SpanText(
                            text = "interface: Best for defining capabilities or behavior contracts.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 1.em).toAttrs()) {
                        SpanText(
                            text = "abstract class: Ideal when you need to share base logic or state across implementations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "What is the Mediator pattern and how does a ViewModel sometimes act as one between UI components?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Mediator centralizes communication between components that would otherwise reference each other directly. A ViewModel often mediates between multiple UI sections (e.g., a filter panel and a result list) that don’t communicate directly but both interact via the shared ViewModel state.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Clean Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "Clean Architecture emphasizes independence between layers. Each layer has a specific responsibility, and dependencies flow inward, making the system easier to maintain and test.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Core Principles",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Layered Structure",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Separate into UI, Domain, and Data layers for clear boundaries.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Dependency Rule",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Only depend inward—inner layers; they never require the outer layers.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Outer layers depend on inner layers only..",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li {
                    Li(Modifier.padding(top = 1.em).toAttrs()) {
                        SpanText(
                            text = "Framework-Independent Core",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Business logic stays pure; no direct links to Android, Room, or Retrofit.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            //UI Layer
            SpanText(
                text = "UI Layer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose, Activities, Fragments Handles what the user sees & interacts with.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@Composable functions and UI logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handles user events and displays state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //Presentation Layer
            SpanText(
                text = "Presentation Layer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModels, Presenters Holds UI state StateFlow<UiState>, and Intent sealed classes, connects UI with domain logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Contains zero business logic beyond state reduction.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Bridge between UI and domain.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The ViewModel holds StateFlow and exposes UiState — a sealed class or data class describing the full screen state.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModels expose immutable UI state via StateFlow, and screens observe and render this state reactively.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Collects events, exposes state, calls UseCases.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //Domain Layer
            SpanText(
                text = "Domain Layer (framework-independent, Pure Kotlin, No Android imports)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UseCases, Encapsulates the business Logic App rules (Validation, calculations, state machines), pure Kotlin, aggregate data from multiple repositories, no Android imports!.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A UseCase encapsulates a single business operation (e.g., GetUserProfileUseCase), called by the ViewModel, keeping business rules out of the ViewModel and reusable across screens. Critics argue it’s unnecessary ceremony for simple CRUD screens with no real business logic — pragmatic teams often skip it for trivial pass-through operations.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use cases own single operations. Repository interfaces are defined here — not implemented.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It defines the contracts (interfaces) that the data layer must fulfill and exposes UseCases as the single entry point for business operations.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Senior-friendly architecture isolates business logic in plain Kotlin classes, separates interfaces from implementations, and avoids direct framework dependencies in core layers. With this setup, unit tests can run quickly and reliably.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UseCases/Interactors: One file per use case. Define the application-specific business rules. This layer acts as a bridge between the presentation and data layers, ensuring that business operations are independent of the user interface. UseCases is to be a mediator between your ViewModels and Repository.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repository: Repository interfaces. We define interfaces in the innermost layer of our project and implement them in the outer layers.  This allows us to replace or change external dependencies without affecting our core business logic.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Model/Entities: Represent the core business objects, independent of frameworks. Business models like Todo which we will reference in business logic use cases and our UI. we use to represent our application’s domain and are completely independent of any external framework or database we’re using. It's responsible for fetching and managing data, whether from a network API, a local database, or both. It handles all the business logic.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Easy to unit test",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    //domain/model 
                    
                    data class User(
                        val id: String,
                        val name: String,
                        val email: String,
                        val avatarUrl: String?,
                        val phone: String?,
                        val memberSince: LocalDate,
                        val tier: MemberTier
                    ) {
                        // Computed properties - shared business logic
                        val displayName: String
                            get() = name.ifBlank { email.substringBefore("@") }
                       
                        val initials: String
                            get() = name.split(" ")
                                .take(2)
                                .mapNotNull { it.firstOrNull()?.uppercaseChar() }
                                .joinToString("")
                                .ifEmpty { email.first().uppercaseChar().toString() }
                      
                        val isPremium: Boolean
                            get() = tier == MemberTier.PREMIUM || tier == MemberTier.ENTERPRISE
                      
                        val maskedEmail: String
                            get() {
                                val parts = email.split("@")
                                if (parts.size != 2) return email
                                val localPart = parts[0]
                                val masked = if (localPart.length <= 2) localPart
                                             else "${localPart.take(2)}${"*".repeat(localPart.length - 2)}"
                                return "$masked@${parts[1]}"
                            }
                       
                        val membershipDuration: String
                            get() {
                                val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
                                val years = now.year - memberSince.year
                                val months = now.monthNumber - memberSince.monthNumber
                                return when {
                                    years > 0 -> "$years year${if (years > 1) "s" else ""}"
                                    months > 0 -> "$months month${if (months > 1) "s" else ""}"
                                    else -> "New member"
                                }
                            }
                    }
                    
                    enum class MemberTier {
                        FREE, PREMIUM, ENTERPRISE;
                        val label: String
                            get() = name.lowercase().replaceFirstChar { it.uppercase() }
                       
                        val badgeColor: Long  // Use Long for color (platform-independent)
                            get() = when (this) {
                                FREE -> 0xFF94A3B8
                                PREMIUM -> 0xFFF59E0B
                                ENTERPRISE -> 0xFF8B5CF6
                            }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    // commonMain/domain/model/Product.kt

                    data class Product(
                        val id: String,
                        val name: String,
                        val description: String,
                        val price: Money,
                        val originalPrice: Money?,  // null = no discount
                        val category: ProductCategory,
                        val rating: Double,
                        val reviewCount: Int,
                        val inStock: Boolean,
                        val imageUrl: String,
                        val tags: List<String>
                    ) {
                        val hasDiscount: Boolean
                            get() = originalPrice != null && originalPrice.amount > price.amount
                            
                        val discountPercentage: Int
                            get() {
                                if (!hasDiscount || originalPrice == null) return 0
                                return ((1 - price.amount / originalPrice.amount) * 100).toInt()
                            }
                            
                        val formattedRating: String
                            get() = "${rating.toString().take(3)} (${reviewCount})"
                            
                        val isHighRated: Boolean
                            get() = rating >= 4.5 && reviewCount >= 10
                    }
                   
                    enum class ProductCategory {
                        ELECTRONICS, CLOTHING, FOOD, BOOKS, HOME, SPORTS, OTHER;
                        val displayName: String
                            get() = name.lowercase().replaceFirstChar { it.uppercase() }
                        val emoji: String
                            get() = when (this) {
                                ELECTRONICS -> "📱"
                                CLOTHING -> "👕"
                                FOOD -> "🍕"
                                BOOKS -> "📚"
                                HOME -> "🏠"
                                SPORTS -> "⚽"
                                OTHER -> "📦"
                            }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$$"""
                    // commonMain/domain/model/Money.kt

                    data class Money(
                        val amount: Double,
                        val currency: Currency
                    ) {
                        /**
                         * Format money with currency symbol.
                         * This is BUSINESS formatting (consistent across platforms).
                         * For LOCALE-SPECIFIC formatting, use platform formatters.
                         */
                        fun formatted(): String {
                            val formatted = amount.toBigDecimalString(2)
                            return when (currency) {
                                Currency.USD -> "$$formatted"
                                Currency.EUR -> "€$formatted"
                                Currency.GBP -> "£$formatted"
                                Currency.EGP -> "EGP $formatted"
                            }
                        }
                        
                        // Arithmetic operators - enforce same currency
                        operator fun plus(other: Money): Money {
                            require(currency == other.currency) { "Cannot add ${currency.name} and ${other.currency.name}" }
                            return copy(amount = amount + other.amount)
                        }
                        
                        operator fun minus(other: Money): Money {
                            require(currency == other.currency) { "Cannot subtract ${currency.name} and ${other.currency.name}" }
                            return copy(amount = amount - other.amount)
                        }
                        
                        operator fun times(multiplier: Int): Money = copy(amount = amount * multiplier)
                        fun isZero(): Boolean = amount == 0.0
                        fun isPositive(): Boolean = amount > 0
                        fun isNegative(): Boolean = amount < 0
                        companion object {
                            fun zero(currency: Currency) = Money(0.0, currency)
                            fun usd(amount: Double) = Money(amount, Currency.USD)
                        }
                    }
                    
                    enum class Currency {
                        USD, EUR, GBP, EGP;
                        val symbol: String
                            get() = when (this) {
                                USD -> "$"
                                EUR -> "€"
                                GBP -> "£"
                                EGP -> "EGP"
                            }
                    }
                    
                    // Kotlin doesn't have BigDecimal in common - helper for formatting
                    private fun Double.toBigDecimalString(decimals: Int): String {
                        val factor = Math.pow(10.0, decimals.toDouble())
                        val rounded = kotlin.math.round(this * factor) / factor
                        val parts = rounded.toString().split(".")
                        val intPart = parts[0]
                        val decPart = (parts.getOrElse(1) { "0" }).padEnd(decimals, '0').take(decimals)
                        return "$intPart.$decPart"
                    }
                    
                    private object Math {
                        fun pow(base: Double, exp: Double): Double = kotlin.math.pow(base, exp)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Explain how the “Single Source of Truth” principle applies to a Repository combining network and local cache.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The local database is treated as the canonical current state the UI observes reactively; network responses update the database rather than being pushed directly to the UI, so the UI always reflects one consistent view of data regardless of which source (cache or fresh network) most recently changed it, avoiding divergent inmemory copies.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Repository vs Use Cases — when is a use case overkill?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Repositories handle data access (fetching and saving), while Use Cases (or Interactors) encapsulate business logic and coordinate those repositories to perform a specific task.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Yes — both use cases and repositories can be overkill when they add layers but no responsibility. Both repositories and use cases can be overkill if they are pass-through wrappers. They become valuable when they encapsulate complexity, not when they merely add layers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A use case that only forwards calls can be unnecessary unless it exists to preserve architectural boundaries or is a stable place for growing business logic. Enforce architecture boundaries. Standard testing pattern. Domain layer isolation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //Data Layer
            SpanText(
                text = "Data Layer - (Getting data and storing it, Not apply business rules, Caching strategy,  Single-source-of-truth strategy)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The data layer is responsible for one thing: getting data and storing it. It does not apply business rules. In this layer, you have an abstract definition of the different data sources, and how they should be used. It fetches, caches, persists, and exposes data through a defined interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Single-source-of-truth strategy: the local database is the UI’s source of truth, and the network layer’s job is only to keep the database fresh.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Mapping DTO → Domain Model, Repositories, Room, Retrofit, DataSources Where your data lives (DB, API, cache).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repository (Remote, Local and Fake Data Source): Repository implementations. A repository interface is defined in the domain layer. The implementation lives in the data layer. Bridge between Domain and Data. A repository can have multiple data sources. A remote data source talks to the API. A local data source talks to Room. Hide whether data is from API, DB, or cache. It is what allows you to swap implementations, write fake repositories for tests, and keep the domain layer ignorant of where data actually comes from. That orchestration logic belongs in the repository, not in the ViewModel and not in the use case.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "DataSource (Remote/Local): Implementations of repositories, which can include network services, databases, or other local data sources. All data source interfaces and data source entities. These entities are different from the domain models, and map directly to request and response objects from the api.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "What Are Use Case Patterns?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A use case pattern is a reusable template that describes how a system should respond to specific user actions or events. Think of them as building blocks for designing your software’s functionality. Instead of starting from scratch each time, you can rely on patterns to standardize workflows, reduce errors, and speed up development.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Authentication — logging in and out",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CRUD Operations — create, read, update, delete",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Notification Handling — sending emails or push notifications",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Dependency rule",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Inner layers (Domain) NEVER import outer layers (Data, Presentation).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Repository Pattern - Single source of truth data handling",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Bridge: Acts as a mediator between your ViewModels and your data. The Facade: Hides the complexity of where data originates. The Golden Rule: The UI should never know how data is fetched, only that it is fetched.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What’s the repository pattern? Why use it?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Single Source of Truth: Centralizes data logic so you don't have duplicate database queries scattered across the app.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Separation of Concerns: Your ViewModel focuses on state; your Repository focuses on data.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Testability: You can easily swap a \"Real Repository\" with a \"Fake\" one for unit testing your UI logic",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The repository abstracts data sources behind an interface. Domain defines ProductRepository (interface). Data implements ProductRepositoryImpl (Retrofit + Room). ViewModel depends only on the interface — data sources are swappable without changing any UI code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A Repository abstracts data source details (network, cache, DB) behind a single interface consumed by the domain/ViewModel layer, centralizing caching/sync logic and decoupling business logic from where data physically comes from — enables swapping/mocking data sources without touching consumers.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Repo To DAO",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unidirectional Flow: ViewModel → Repository → DAO → Database.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CRUD Execution: The Repository calls the DAO to Create, Read, Update, or Delete data.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Refactor Immunity: Switching databases only changes the Repository layer, your ViewModels stay untouched.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    //1
                    data class User(
                        val name: String,
                        val surname: String
                    )

                    data class UserDto(
                        val fullName: String
                    )

                    // DTO → Domain
                    fun UserDto.toUser(): User {
                        val parts = fullName.split(" ")
                        return User(
                            name = parts[0],
                            surname = parts[1]
                        )
                    }

                    // Domain → DTO
                    fun User.toUserDto(): UserDto {
                        return UserDto("$name $surname")
                    }
                    
                    //2
                    data class UserDto(
                        val fullName: String
                    )

                    // DTO → Domain
                    fun UserDto.toUser(): User {
                        val parts = fullName.trim().split(" ")

                        return User(
                            name = parts.getOrElse(0) { "" },
                            surname = parts.getOrElse(1) { "" }
                        )
                    }

                    // Domain → DTO
                    fun User.toUserDto(): UserDto {
                        return UserDto(
                            fullName = "$name $surname".trim()
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "internal class Clean Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/xephosbot/AnilibriaRefresh.git
            KotlinCode(
                code = """
                    //internal visible anywhere inside the same module (e.g., a Gradle source set or Maven project).
                    
                    //domain → repository
                    interface AuthRepository { }
                    
                    //data → repository
                    internal class DefaultAuthRepository(
                    ) : AuthRepository { }
                    
                    //data → di → RepositoryModule.kt
                    internal val repositoryModule = module {
                        singleOf(::DefaultAuthRepository) { bind<AuthRepository>() }
                    }
                    
                    internal fun createHttpClient(
                    ): HttpClient = HttpClient { }
                    
                    //Action for ViewModel
                    internal sealed interface SearchScreenAction {
                        data class ToggleGenre(val genre: Genre) : SearchScreenAction
                        data class TogglePublishStatus(val publishStatus: PublishStatus) : SearchScreenAction
                        data class ToggleProductionStatus(val productionStatus: ProductionStatus) : SearchScreenAction
                        data class ToggleReleaseType(val releaseType: ReleaseType) : SearchScreenAction
                        data class ToggleSeason(val season: Season) : SearchScreenAction
                        data class UpdateSortingType(val sortingType: SortingType) : SearchScreenAction
                        data class UpdateYearsRange(val years: IntRange) : SearchScreenAction
                        data class ToggleAgeRating(val ageRating: AgeRating) : SearchScreenAction
                        data class ShowErrorMessage(
                            val error: Throwable,
                            val onConfirmAction: () -> Unit,
                        ) : SearchScreenAction
                    }
                    
                    internal class SearchViewModel(): ViewModel() {
                          fun onAction(action: SearchScreenAction) {
                            when (action) {
                            }
                          }
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "Koin Clean Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "single: Use for a single instance throughout the app lifecycle. Use for a new instance each time it is requested. Best for long-lived, shared objects that shouldn't be recreated.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "factory: Use for a new instance each time it is requested. Best for lightweight, stateless objects that shouldn't hold shared state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Repository, Room Database, API service - single",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data (Repository, DataSource) - singleOf",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Domain (UseCase, Mappers, Stateless utilities) - factoryOf",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Presentation (ViewModel) - viewModelOf",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    kotlin = "2.3.20-Beta2"
                    koin = "4.2.0-RC1"
                    koinAnnotations = "2.3.1"
                    koin-plugin = "0.3.0"
                    
                    [libraries]
                    koin-core = { module = "io.insert-koin:koin-core", version.ref = "koin" }
                    koin-android = { module = "io.insert-koin:koin-android", version.ref = "koin" }
                    koin-compose = { module = "io.insert-koin:koin-compose", version.ref = "koin" }
                    koin-compose-viewmodel = { module = "io.insert-koin:koin-compose-viewmodel", version.ref = "koin" }
                    koin-annotations = { module = "io.insert-koin:koin-annotations", version.ref = "koinAnnotations" }
                    
                    koin-compiler = { id = "io.insert-koin.compiler.plugin", version.ref = "koin-plugin" }
                    
                    plugins {
                        alias(libs.plugins.koin.compiler)
                    }
                    
                    koinCompiler {
                        userLogs = true
                        debugLogs = false
                        dslSafetyChecks = true
                    }
                    
                    //HttpClient
                    fun createHttpClientWithConfig(): HttpClient { }
                    //single { createHttpClientWithConfig() } 
                    
                    //DateTime
                    private val clock: Clock by inject()
                    val currentDate = clock.millis().toLocalDate()
                    internal val appModule = module {
                        single { Clock.systemUTC() }
                    }
                    
                    //NEW COMPILER PLUGIN: single‹Class>(), factory<Class>(), scoped<Class>(), viewModel<VM>()
                    //CLASSIC AUTOWIRE: singleOf(::Class), factoryOf(::Class), scopedOf(::Class), viewModelOf(::VM)
                    
                    single { create(::createHttpClientWithConfig) } // Safe function builder call
                    
                    single<PaymentRepositoryImpl>() bind PaymentRepository:: class

                    single<ProcessPaymentUseCase>()
                    single<AppStateManager>()
                    //factory<ProcessPaymentUseCase>()

                    //Parameter
                    viewModel { parameters ->
                        //paymentId = parameters.get()
                        ConfirmPaymentViewModel(get())
                    }
                    viewModel<StatusPaymentViewModel>()
                    
                    //withOptions
                    single<MuseumRepository>() withOptions { createdAtStart() }
                    """.trimIndent()
            )

            //https://github.com/RznNike/DemoKMP.git (Good API Calling (webSocketManager, ktorfit, Common UseCase, Common ViewModel))
            KotlinCode(
                code = $$"""
                       internal val appModule = module {

                           single<CoroutineScopeProvider> {
                               object : CoroutineScopeProvider {
                                   override val ui = MainScope()
                                   override val default = CoroutineScope(Dispatchers.Default)
                                   override val io = CoroutineScope(Dispatchers.IO)
                                   override val unconfined = CoroutineScope(Dispatchers.Unconfined)
                               }
                           }

                           single<DispatcherProvider> {
                               object : DispatcherProvider {
                                   override val ui = Dispatchers.Main
                                   override val default = Dispatchers.Default
                                   override val io = Dispatchers.IO
                                   override val unconfined = Dispatchers.Unconfined
                               }
                           }
                       }
                       
                       interface CoroutineScopeProvider {
                           val ui: CoroutineScope
                           val default: CoroutineScope
                           val io: CoroutineScope
                           val unconfined: CoroutineScope
                       }
                       
                       interface DispatcherProvider {
                           val ui: CoroutineDispatcher
                           val default: CoroutineDispatcher
                           val io: CoroutineDispatcher
                           val unconfined: CoroutineDispatcher
                       }
                       
                       //FlowUseCase.kt
                       import kotlinx.coroutines.flow.Flow
                       import kotlinx.coroutines.flow.flowOn
                       
                       abstract class FlowUseCase<R>(private val dispatcherProvider: DispatcherProvider) {
                           suspend operator fun invoke(): Flow<R> {
                               return execute().flowOn(dispatcherProvider.default)
                           }

                           @Throws(RuntimeException::class)
                           protected abstract suspend fun execute(): Flow<R>
                       }
                       
                       //FlowUseCaseWithParams.kt
                       abstract class FlowUseCaseWithParams<in P, R>(private val dispatcherProvider: DispatcherProvider) {
                           suspend operator fun invoke(parameters: P): Flow<R> {
                               return execute(parameters).flowOn(dispatcherProvider.default)
                           }

                           @Throws(RuntimeException::class)
                           protected abstract suspend fun execute(parameters: P): Flow<R>
                       }
                       
                       //UseCase.kt
                       abstract class UseCase<R>(private val dispatcherProvider: DispatcherProvider) {
                           suspend operator fun invoke(): UseCaseResult<R> {
                               return try {
                                   withContext(dispatcherProvider.default) {
                                       UseCaseResult(data = execute())
                                   }
                               } catch (e: Exception) {
                                   UseCaseResult(error = e)
                               }
                           }

                           @Throws(RuntimeException::class)
                           protected abstract suspend fun execute(): R
                       }
                       
                       //UseCaseResult.kt
                       class UseCaseResult<out R>(
                           val data: R? = null,
                           val error: Exception? = null
                       ) {
                           override fun toString(): String {
                               return error?.let {
                                   "Error[exception=$error]"
                               } ?: run {
                                   "Success[data=$data]"
                               }
                           }

                           suspend fun process(
                               onSuccessCallback: suspend (R) -> Unit,
                               onErrorCallback: (suspend (Exception) -> Unit)? = null
                           ): UseCaseResult<R> {
                               error?.let {
                                   onErrorCallback?.invoke(it)
                               } ?: run {
                                   data?.let {
                                       onSuccessCallback(it)
                                   } ?: run {
                                       onErrorCallback?.invoke(
                                           Exception("Unexpected exception in UseCaseResult: data=null and error=null")
                                       )
                                   }
                               }

                               return this
                           }
                       }
                       
                       //UseCaseWithParams.kt
                       abstract class UseCaseWithParams<in P, R>(private val dispatcherProvider: DispatcherProvider) {
                           suspend operator fun invoke(parameters: P): UseCaseResult<R> {
                               return try {
                                   withContext(dispatcherProvider.default) {
                                       UseCaseResult(data = execute(parameters))
                                   }
                               } catch (e: Exception) {
                                   UseCaseResult(error = e)
                               }
                           }

                           @Throws(RuntimeException::class)
                           protected abstract suspend fun execute(parameters: P): R
                       }

                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Database driver is provided by platform-specific modules (expect/actual)
                    single<SqlDriver> { createDatabaseDriver() }
                     
                    // Database instance
                    single {
                      WorkoutDatabase(
                          driver = get()
                      )
                    }
                    
                    single { createHttpClient(httpClientEngine = get()) }
                    
                    single { HttpClientFactory(get()).create(get()) }
                    
                    single<HttpClient> {createHttpClient(OkHttp.create()) }
                    
                    single<DataStore<Preferences>> {
                        PreferenceDataStoreFactory.create(
                            produceFile = { get<Context>().preferencesDataStoreFile("auth_prefs") }
                        )
                    }
                    
                    //DSL
                    val appModule = module {
                        single { createHttpClientWithConfig() }
                        singleOf(::ApiServiceImpl) bind ApiService::class
                        single<TokenDataStore> { createTokenDataStore() }

                        singleOf(::PaymentRepositoryImpl) bind PaymentRepository::class
                        factoryOf(::PassAuthUseCaseImpl) bind PassAuthUseCase::class

                        factoryOf(::ProcessPaymentUseCase)
                        singleOf(::AppStateManager)

                        viewModelOf(::ConfirmPaymentViewModel)
                        viewModelOf(::StatusPaymentViewModel)
                    }
                    
                    //https://github.com/TouhidApps/MoneyManager-Kotlin-Multiplatform-KMP-CMP
                    viewModel { TransactionViewModel(get(), get(), get(), get()) } bind HomeContract::class
                    """.trimIndent()
            )

            //https://github.com/NsAveek/InventoryManagement.git
            KotlinCode(
                code = """
                    /**
                     * Base use case for single-shot operations (e.g., login, create account).
                     *
                     * @param P Parameters input type
                     * @param R Result data type
                     */
                    abstract class UseCase<in P, R>(
                        private val dispatcher: CoroutineDispatcher,
                    ) {
                        suspend operator fun invoke(params: P): Result<R> {
                            return try {
                                withContext(dispatcher) {
                                    execute(params)
                                }
                            } catch (e: Exception) {
                                Result.Error(e.message ?: "Unknown error", e)
                            }
                        }

                        protected abstract suspend fun execute(params: P): Result<R>
                    }

                    /**
                     * Base use case for operations that return a Flow (e.g., observing data).
                     *
                     * @param P Parameters input type
                     * @param R Result data type
                     */
                    abstract class FlowUseCase<in P, R>(
                        private val dispatcher: CoroutineDispatcher,
                    ) {
                        operator fun invoke(params: P): Flow<Result<R>> {
                            return execute(params)
                                .catch { e -> emit(Result.Error(e.message ?: "Unknown error", e)) }
                                .flowOn(dispatcher)
                        }

                        protected abstract fun execute(params: P): Flow<Result<R>>
                    }

                    /**
                     * Use as params type when a use case doesn't need parameters.
                     */
                    object NoParams
                    
                    /**
                     * A generic wrapper for representing the result of an operation.
                     * Used across all layers (data, domain, presentation) to communicate outcomes.
                     */
                    sealed interface Result<out T> {

                        data class Success<T>(val data: T) : Result<T>

                        data class Error(
                            val message: String,
                            val throwable: Throwable? = null,
                        ) : Result<Nothing>

                        data object Loading : Result<Nothing>
                    }

                    /**
                     * Maps the success value to a different type.
                     */
                    inline fun <T, R> Result<T>.map(transform: (T) -> R): Result<R> = when (this) {
                        is Result.Success -> Result.Success(transform(data))
                        is Result.Error -> this
                        is Result.Loading -> this
                    }

                    /**
                     * Executes [onSuccess] if this is a Success, [onError] if Error.
                     */
                    inline fun <T> Result<T>.fold(
                        onSuccess: (T) -> Unit,
                        onError: (String, Throwable?) -> Unit,
                    ) {
                        when (this) {
                            is Result.Success -> onSuccess(data)
                            is Result.Error -> onError(message, throwable)
                            is Result.Loading -> Unit
                        }
                    }

                    /**
                     * Returns the success data or null.
                     */
                    fun <T> Result<T>.getOrNull(): T? = when (this) {
                        is Result.Success -> data
                        else -> null
                    }

                    /**
                     * Returns the success data or the provided default.
                     */
                    fun <T> Result<T>.getOrDefault(default: T): T = when (this) {
                        is Result.Success -> data
                        else -> default
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Useful Patterns in Clean Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Repository Pattern: Abstract data sources (Room, Retrofit). Single source of truth. Abstract data source. Test-friendly",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Factory Pattern: Create complex objects cleanly",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Observer Pattern: Reactive data flow with Flow/LiveData.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Strategy Pattern: Swap algorithms at runtime (payments, auth).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Dependency Injection: Wire everything together (Hilt/Koin)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Mapper Pattern: Convert between layers (Entity ↔ DTO ↔ UI Model",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Facade Pattern: Simplify complex subsystems.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Result Wrapper: Handle success/error states elegantly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Data Flows in Clean Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "User taps a button: UI Layer",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel receives the event: Presentation Layer",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel calls a UseCase: Domain Layer",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UseCase interacts with Repository: Data Layer",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Repository fetches/saves data: Room, Retrofit, etc.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Result flows back up as UI State: To UI Layer",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Clean separation: UI triggers -> Business logic executes -> Data persists -> UI updates",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "ViewModel processes Intent",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Calls UseCase",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Updates loading state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Collects data",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Emits updated UI state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "In a large Android codebase, how do you identify violations of SOLID principles?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To identify violations of SOLID in a large Android codebase, you should focus on both architecture and individual class design. Look for:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Classes doing too many unrelated things (SRP violation)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Frequent modifications in classes due to minor changes (OCP violation)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Subclasses that don’t behave consistently with their parents (LSP violation)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Interfaces forcing unused method implementations (ISP violation)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "DIP violation - Tight coupling with concrete classes instead of abstractions. High-level modules should not depend on low-level modules. Both should depend on abstractions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Tools like Detekt, Lint, or SonarQube can help surface such issues. Also, watch for large classes, code duplication, and unclear dependencies.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Content coupling",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Content coupling (Runtime Error): one module reaching directly into another's internals, no boundary at all",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    var client = ApiClient()
                    client.setAuthToken(token)  //if token missing to write
                    client.setBaseUrl(baseUrl)
                    client.setTimeout(5000)
                    """.trimIndent()
            )

            SpanText(
                text = "Tight Coupling",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Problem: When each class depends directly on the details of another class. Any small change → a big impact on the rest of the system.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel Knows Repository Details",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repository has logic that is not his",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "dependencies directly linked to each other",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    //Tight Coupling
                    class UserRepository {
                        fun getUser(): String {
                            return "Kamlesh"
                        }
                    }

                    class UserViewModel : ViewModel() {
                        private val repository = UserRepository() // ❌ tightly coupled

                        fun loadUser(): String {
                            return repository.getUser()
                        }
                    }
                    
                    @Composable
                    fun UserScreen(viewModel: UserViewModel = UserViewModel()) { // ❌ also tight coupling
                   
                    }
                    
                    //Can’t inject fake repository
                    //Unit testing becomes painful
                    //Want API instead of local data? must edit ViewModel
                    
                    //Real example
                    //A mobile app directly calling a specific payment gateway class (e.g., Razorpay class inside UI)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Loosely Coupled
                    interface UserRepository {
                        fun getUser(): String
                    }
                    
                    class UserRepositoryImpl : UserRepository {
                        override fun getUser(): String {
                            return "Kamlesh"
                        }
                    }
                    
                    class UserViewModel(
                        private val repository: UserRepository
                    ) : ViewModel() {

                        fun loadUser(): String {
                            return repository.getUser()
                        }
                    }
                    
                    //di
                    @HiltViewModel
                    class UserViewModel @Inject constructor(
                        private val repository: UserRepository
                    ) : ViewModel()
                    
                    //UI doesn’t know implementation
                    //ViewModel doesn’t know data source
                    
                    //Real example
                    //App depends on PaymentGateway interface → can switch between Razorpay, Stripe, etc
                    """.trimIndent()
            )

            SpanText(
                text = "Can you explain the Interface Segregation Principle in the context of Jetpack Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Interface Segregation Principle says it’s better to have multiple small, focused interfaces than one large, general-purpose one. A class should only implement what it actually needs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Jetpack Compose or MVVM design pattern, you might follow this by splitting view-related responsibilities into small interfaces like:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "LoadingHandler, ErrorHandler, NavigationHandler",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each interface serves one purpose — so ViewModels or UI components only depend on what they use.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How would you apply the Open/Closed Principle to handle different API response types cleanly?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Open/Closed Principle says code should be open for extension but closed for modification. To handle different API response types cleanly, I use sealed classes or polymorphism.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead of writing multiple (if or when statements), I define a base class like ApiResponse, and create separate subclasses for Success, Error, or Empty responses. That way, new response types can be added without touching the existing logic — just by extending the class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What patterns help you maintain the Liskov Substitution Principle in dynamic UI or business rules?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To maintain LSP in dynamic UIs or complex business logic, patterns like Strategy and true Polymorphism are key — they allow interchangeable behavior without breaking expectations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Also, Kotlin’s sealed classes and interface segregation help model different UI states or rules safely, ensuring subclasses can substitute their parents without side effects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Assume you have a class that has a subclass in an app. You can represent the usage of the base class with the subclass, and you don’t need to disrupt the app for this.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Domain-Centric Design",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Domain layer is the most stable part of the application. In this architecture, it contains:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Entities are data classes representing the core business model.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repository Interfaces are contracts defining data operations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use Cases are single-responsibility classes that execute specific business actions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By strictly defining Repository interfaces in the Domain layer, the application achieves \"Inversion of Control.\" The Data layer (outer) depends on the Domain layer (inner) to implement these interfaces, ensuring the business logic remains ignorant of whether the data comes from a SQL database, a REST API, or a mock object during testing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is the difference between ViewModel and Repository? (MVVM)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Holds UI state",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Survives configuration changes (like screen rotation)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Calls the Repository to fetch data",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Exposes data to UI using LiveData, Flow, or StateFlow",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel converts the cold Flow from the repository into a hot StateFlow that the UI can observe.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repository – The Data Manager",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It decides whether the data should be fetched from: API, Local database (Room), Cache, Multiple sources",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Why use map in data model?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "map transforms a collection into another by applying a function to each element.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Repositories interface / abstract (Choose abstract or interface - Separation of Concerns - Single Source of Truth - ViewModel call Repository - Repository hide its data sources - Decoupling)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Create an interface or abstract class to define the available data operations. Interfaces are preferred for simple contracts, while abstract classes are used if you need shared state or common logic.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A repository is a very common pattern for data source abstraction. You use it to abstract away all the data sources you want. Anything that calls the repository can access data from those sources, but will never know which sources even exist.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "In the domain layer, you won’t actually implement a repository. Instead, you’ll only have a repository interface. This allows you to invert the dependency on the layers, making the data layer depend on the domain layer, instead of the other way around.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Decoupling: The UI layer (like a ViewModel) interacts only with the repository abstraction, making it independent of specific database or API changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Abstraction: Hides the details of data source implementations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Separation of Concerns: Your ViewModel shouldn’t know how to fetch data. It should only ask for data. The Repository hides all the “how” and just provides the result.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Single Source of Truth: Repositories make sure your app has one consistent place to fetch data. For example, even if data comes from both API and DB, the Repository decides which one to use.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Maintainability: Simplifies changing or adding data sources. To allow switching data sources (e.g., mock vs real) without affecting Ul code.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Testability: They hide data-source details from higher layers and make testing easier.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A repository is an abstract layer to access data sources like networks or local databases. Repository mediates between the domain (business logic) and data mapping (database, network, etc.) layers, acting like an in-memory domain object collection.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Repository provides a simple interface to receive domain objects and manage their lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "At the same time, both definitions have an important common part — the domain objects. In the Clean Architecture and the Domain Driven Design a domain model is the center of each application. This model is built of objects which we usually call Entities.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In order to perform some business logic, we need to somehow get these objects. Very often, our business logic also needs to modify these objects, add new ones or delete the existing ones. Here we see the true responsibility of Repositories — they provide us with domain objects and manage their lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you’re familiar with SOLID principles, you would know that high-level modules should not depend on low-level modules. Both should depend on abstractions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How would you structure a feature module using SOLID principles and Clean Architecture?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To structure a feature like Task or Profile using SOLID and Clean Architecture, I’d split it into these layers:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Core modules - Provide foundational utilities, like coroutines for async tasks or HTTP clients for network requests.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Repositories: Do one job — save and fetch aggregates from persistence (database, cache, file system, etc.). Nothing more. Repository as a middleman between your app and your data sources.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Presentation Layer – ViewModels and UI state. Talks only to UseCases. Declare your dependencies inside the constructor and, very importantly – have it depend on the abstraction.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                             class LoginViewModel : ViewModel() {
                                 // WRONG - initializing dependency inside 
                                 private val repository = KtorAuthRepository()
                             }

                             class LoginViewModel(
                                 // CORRECT - dependency is passed to the ViewModel
                                 private val repository: AuthRepository
                             ) : ViewModel() { }
                        """.trimIndent()
                    )

                    //https://github.com/Hussienfahmy/GPA_Manager.git
                    KotlinCode(
                        code = """
                            /**
                             * @param T is the type of the event that will be received by [onEvent]
                             * @param R is the type of the state that will be used by the UI
                             */
                            abstract class UiViewModel<T, R>(initialState: () -> R) : ViewModel() {

                                protected val _uiEvent = Channel<UiEvent>()
                                val uiEvent = _uiEvent.receiveAsFlow()

                                abstract fun onEvent(event: T)

                                var state = mutableStateOf(initialState())
                                    private set
                            }
                            
                            sealed class GPAEvent {
                                data class UpdateGPASystem(val system: GPA.System) : GPAEvent()
                            }
                            
                            sealed class GPAState {
                                object Loading : GPAState()
                                data class Loaded(val gpa: GPA) : GPAState()
                            }
                            
                            class GPASettingsViewModel(
                                observeGPA: ObserveGPASettings,
                                private val updateGPASystem: UpdateGPASystem,
                                private val analyticsLogger: AnalyticsLogger,
                            ) : UiViewModel<GPAEvent, GPAState>(initialState = { GPAState.Loading }) {
                            }
                        """.trimIndent()
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Domain Layer – UseCases and repository interfaces. Knows nothing about data sources. contain reusable business logic that doesn't naturally live inside one entity (e.g. checking stock availability.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Data Layer – Manages and provides data for the domain layer. Repository implementations and data sources (API, DB). Depends only on abstractions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This structure ensures:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Single Responsibility for each class.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Dependency Inversion between domain and data.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A testable, modular, and scalable feature module.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://medium.com/@VolodymyrSch/the-complexities-of-clean-architecture-use-cases-71ac89ea8b40
            SpanText(
                text = "Facade pattern  use clean architecture layer",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A facade is a class that provides a simple interface to a complex subsystem which contains lots of moving parts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        //InventoryService: Manages stock levels and product availability.
                        //ShippingService: Handles logistics and delivery processes.
                        //BillingService: Processes payments and generates invoices.
                        //NotificationService: Sends alerts and confirmations to customers.

                        class WarehouseFacade(
                            private val inventoryService: InventoryService,
                            private val shippingService: ShippingService,
                            private val billingService: BillingService,
                            private val notificationService: NotificationService
                        ) {
                            fun processOrder(order: Order) {
                                if (inventoryService.isInStock(order.productId)) {
                                    billingService.charge(order)
                                    shippingService.ship(order)
                                    notificationService.sendConfirmation(order)
                                } else {
                                    notificationService.sendOutOfStockAlert(order)
                                }
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you want to maintain the separation of concerns and follow the clean architecture layering principles completely without falling into the trap of excessive one-liner use cases, consider using a facade pattern.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        class UserFacade(
                           private val userRepository: UserRepository,
                           private val userMapper: UserMapper
                        ) {

                         fun getUser(userConfig: UserConfig): Result<User> {
                           return userRepository.getUser(userConfig)
                         }

                         fun getAllUsers(): Result<List<User>> {
                           return userRepository.getUsers()
                         }

                         fun getAllPremiumUsers(): Result<List<User>> {
                           return userRepository.getPremiumUsers()
                         }

                         fun addUser(user: User): Result<User> {
                           return userRepository.addUser(user)
                         }

                         fun deleteUser(user: User): Result<User> {
                            return userRepository.deleteUser(user)
                         }

                         fun updateUser(user: User): Result<User> {
                            return userRepository.updateUser(user)
                         }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = $$"""
                        //UseCase, does it violate SRP

                        class UserRegistrationFlowUseCase(
                            private val saveUserUseCase: SaveUserUseCase,
                            private val prepareNewUserUseCase: PrepareNewUserUseCase,
                            private val userFollowUpUseCase: UserFollowUpUseCase,
                            private val sendWelcomeEmailUseCase: SendWelcomeEmailUseCase,
                            private val setAppThemeUseCase: SetAppThemeUseCase,
                        ) {

                          operator fun invoke(userDetails: UserDetails): Result<User> {
                              val userResult = prepareNewUserUseCase.prepareUser(userDetails)
                              if (userResult.isError()) {
                                 return userResult
                              }
                              val user = userResult.get()

                              saveUserUseCase(user)
                              sendWelcomeEmailUseCase(user)
                              userFollowUpUseCase(user)
                              setAppThemeUseCase(user)
                              return Result.success(user)
                          }
                        }

                        class PromotionEligibilityUseCase(
                            private val emailPromotionEligibilityUseCase: EmailPromotionEligibilityUseCase,
                        ) {

                          fun checkEligibility(userDetails: UserDetails): Boolean {
                              val isEmailEligible = emailPromotionEligibilityUseCase(userDetails.email)
                              val isLocationEligible = userDetails.location == "USA" // Assume promotional eligibility for USA
                              return isEmailEligible && isLocationEligible
                          }

                        }

                        class EmailPromotionEligibilityUseCase {
                          operator fun invoke(email: String): Boolean {
                              val emailRegex = "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,6}$"
                              return email.endsWith("@example.com") && email.matches(emailRegex.toRegex())
                          }
                        }

                        class PrepareNewUserUseCase(
                            private val securityService: SecurityService,
                            private val getStarterPackUseCase: GetStarterPackUseCase,
                            private val promotionEligibilityUseCase: PromotionEligibilityUseCase,
                        ) {

                          fun prepareUser(userDetails: UserDetails): Result<User> {
                              if (securityService.weak(password = userDetails.password)) {
                                 return Result.failure(Exception("Password is weak"))
                              }
                              val isPromotional = promotionEligibilityUseCase.checkEligibility(userDetails)
                              val userSettings = UserSettings("en-US", receiveNewsletters = isPromotional)
                              val starterPack = getStarterPackUseCase(userDetails.location, isPromotional)
                              val encryptedPassword = securityService.encryptPassword(userDetails.password)
                              return Result.success(User(
                                  id = 0,
                                  name = userDetails.name,
                                  email = userDetails.email,
                                  password = encryptedPassword,
                                  location = userDetails.location,
                                  isPromotional = isPromotional,
                                  settings = userSettings,
                                  starterPack = starterPack
                                )
                              )
                          }
                        }

                        class GetStarterPackUseCase(private val promotionsService: PromotionsService) {

                          operator fun invoke(location: String, isPromotional: Boolean): StarterPack {
                              return if (isPromotional)
                                  promotionsService.getPromotionalStarterPack(location)
                              else
                                  promotionsService.getDefaultStarterPack(location)
                          }
                        }

                        class SaveUserUseCase(private val userRepository: UserRepository) {

                          operator fun invoke(user: User): User {
                              return userRepository.save(user)
                          }
                        }

                        class SendWelcomeEmailUseCase(
                            private val emailService: EmailService
                        ) {

                          operator fun invoke(user: User) {
                              emailService.sendWelcomeEmail(user.email, user.isPromotional)
                          }
                        }

                        class SetAppThemeUseCase(
                            private val appThemeRepository: AppThemeRepository
                        ) {

                          operator fun invoke(user: User) {
                              if (user.isPromotional)
                                  appThemeRepository.save(user.id, "dark")
                              else
                                  appThemeRepository.save(user.id, "light")
                          }
                        }

                        class UserFollowUpUseCase(
                            private val engagementTracker: EngagementTracker
                        ) {

                           operator fun invoke(user: User) {
                               engagementTracker.schedulePersonalizedFollowUps(user.id, user.email, user.isPromotional)
                           }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The facade pattern — makes the subsystem easier to use by encapsulating the complexity behind a single, more cohesive interface. Instead of having multiple one-liner use cases scattered throughout the codebase, a facade consolidates these operations. This reduces the redundancy of having many use cases that essentially do the same thing — interact with the data layer.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //UseCase common
            SpanText(
                text = "Kotlin UseCase",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //2. https://github.com/droidwithme/product-catalog.git
            KotlinCode(
                code = """
                //1    
                interface UseCase<out Type, in Params> where Type: Any {
                    suspend operator fun invoke(params: Params): Result<Type>
                }
                
                //2
                public interface UseCase<in Param, out Output> {
                    public operator fun invoke(param: Param): Flow<Result<Output>>
                }

                class GetAllNoteUsecase(
                    private val noteRepository: NoteRepository
                ) : UseCase<List<EntityNote>, Unit> {
                    override suspend fun invoke(params: Unit): Result<List<EntityNote>>
                            = noteRepository.getAll()
                }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Error.kt
                    import kotlinx.serialization.Serializable

                    class ApiException(override val message: String): Exception(message)

                    @Serializable
                    data class ApiError(val message: String)
                    
                    //BaseUseCase.kt
                    import io.ktor.client.network.sockets.SocketTimeoutException
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.flow
                    import kotlinx.coroutines.flow.flowOn

                    /**
                     * Extend your UseCase classes with [UseCase] if you do not want to wrap your result with [UseCaseResult]
                     */
                    abstract class UseCase<in REQUEST, out RESPONSE> {

                        operator fun invoke(request: REQUEST): RESPONSE? {
                            return runCatching {
                                execute(request)
                            }.getOrNull()
                        }

                        abstract fun execute(request: REQUEST): RESPONSE
                    }

                    /**
                     * Extend your UseCase classes with [BaseUseCase] for a single shot response
                     * @see UseCaseResult
                     */
                    abstract class BaseUseCase<in REQUEST, out RESPONSE>(
                        open val dispatcher: CoroutineDispatcher = Dispatchers.IO
                    ) {
                        suspend operator fun invoke(request: REQUEST): UseCaseResult<RESPONSE> {
                            return try {
                                withContext(dispatcher) {
                                    UseCaseResult.Success(execute(request))
                                }
                            } catch (e: Exception) {
                                getUseCaseResultFromThrowable(e)
                            }
                        }

                        abstract suspend fun execute(request: REQUEST): RESPONSE
                    }

                    /**
                     * Extend your UseCase with [BaseFlowUseCase] if you want to wrap the response in a [Flow]
                     * @see UseCaseResult
                     */
                    abstract class BaseFlowUseCase<in REQUEST, out RESPONSE>(
                        open val dispatcher: CoroutineDispatcher = Dispatchers.IO
                    ) {
                        operator fun invoke(request: REQUEST): Flow<UseCaseResult<RESPONSE>> {
                            return flow {
                                val result = try {
                                    UseCaseResult.Success(execute(request))
                                } catch (e: Exception) {
                                    getUseCaseResultFromThrowable(e)
                                }

                                emit(result)
                            }.flowOn(dispatcher)
                        }

                        abstract suspend fun execute(request: REQUEST): RESPONSE
                    }

                    private fun <DATA>getUseCaseResultFromThrowable(exception: Throwable): UseCaseResult<DATA> {
                        return when (exception) {
                            is CancellationException -> throw exception
                            is SocketTimeoutException -> UseCaseResult.NetworkError
                            is ApiException -> UseCaseResult.Error(message = exception.message)
                            else -> UseCaseResult.Unknown
                        }
                    }

                    sealed interface UseCaseResult<out DATA> {
                        data class Success<DATA>(val data: DATA): UseCaseResult<DATA>
                        data object NetworkError: UseCaseResult<Nothing>
                        data class Error(val message: String): UseCaseResult<Nothing>
                        data object Unknown: UseCaseResult<Nothing>
                    }
                    
                    //CurrentUser.kt
                    @Serializable
                    data class CurrentUser(
                        val id: String,
                        val name: String,
                        val email: String,
                        val profilePic: String,
                        val isLoggedIn: Boolean
                    )

                    fun User.toCurrentUser(): CurrentUser {
                        return CurrentUser(
                            id = id,
                            name = name.orEmpty(),
                            email = email.orEmpty(),
                            profilePic = profilePic.orEmpty(),
                            isLoggedIn = loginStatus.isLoggedIn()
                        )
                    }

                    enum class LoginStatus {
                        Y, N;

                        fun isLoggedIn(): Boolean = this == Y
                    }
                    
                    //UserRepository.kt
                    internal interface UserRepository {
                        fun observeCurrentUser(): Flow<CurrentUser>
                    }
                    
                    //ObserveCurrentUserUseCase.kt
                    import kotlinx.coroutines.flow.Flow
                    import org.koin.core.annotation.Factory

                    abstract class ObserveCurrentUserUseCase : UseCase<Unit, Flow<CurrentUser>>()

                    @Factory
                    internal class ObserveCurrentUserUseCaseImpl(private val userRepository: UserRepository): ObserveCurrentUserUseCase() {
                        override fun execute(request: Unit): Flow<CurrentUser> {
                            return userRepository.observeCurrentUser()
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Handle Duplicate State Handling for UseCases",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Wrong Way
                    class HomeViewModel(
                        private val completionUseCase: CompletionUseCase
                    ) : ViewModel() {
                        
                        private val _state = MutableStateFlow<CompletionUiState>(CompletionUiState.Idle)
                        val state: StateFlow<CompletionUiState> = _state

                        fun requestCompletion(input: String) {
                            viewModelScope.launch {
                                completionUseCase(input)
                                    .onStart { _state.value = CompletionUiState.Loading }
                                    .catch { _state.value = CompletionUiState.Error(it) }
                                    .collect { result ->
                                        _state.value = CompletionUiState.Success(result)
                                    }  
                            }
                        }
                    }
                    
                    //Right Way
                    sealed class CompletionUiState {
                        object Idle : CompletionUiState()
                        object Loading : CompletionUiState()
                        data class Success(val result: String) : CompletionUiState()
                        data class Error(val throwable: Throwable) : CompletionUiState()
                    }
                    
                    interface CompletionStateHandler {
                        val completionState: StateFlow<CompletionUiState>
                        fun requestCompletion(input: String)
                    }
                    
                    class DefaultCompletionStateHandler(
                        private val completionUseCase: CompletionUseCase,
                        private val coroutineScope: CoroutineScope
                    ) : CompletionStateHandler {

                        private val _state = MutableStateFlow<CompletionUiState>(CompletionUiState.Idle)
                        override val completionState: StateFlow<CompletionUiState> = _state

                        override fun requestCompletion(input: String) {
                            coroutineScope.launch {
                                completionUseCase(input)
                                    .onStart { _state.value = CompletionUiState.Loading }
                                    .catch { _state.value = CompletionUiState.Error(it) }
                                    .collect { result ->
                                        _state.value = CompletionUiState.Success(result)
                                    }
                            }
                        }
                    }
                    
                    class HomeViewModel(
                        completionUseCase: CompletionUseCase
                    ) : ViewModel(), CompletionStateHandler by DefaultCompletionStateHandler(completionUseCase, viewModelScope)
                    """.trimIndent()
            )

            SpanText(
                text = "Why Not Define invoke() in the Data Layer?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The invoke() method, often used for calling a function, is generally not defined in the data layer of an application architecture. This is because the data layer should primarily focus on data access and storage, while invoke()-like functionality, such as running business logic or use cases, typically belongs to the domain or application layer.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The data layer's responsibility is to provide concrete implementations of the interfaces defined in the domain layer. Introducing invoke() functions here would blur the lines between data handling and business logic, violating the separation of concerns principle. Moreover, the data layer should not contain business logic or dictate how data is used; that's the domain layer's role. Keeping invoke() in the domain layer ensures that business rules remain centralized and testable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }


            //Mapper
            SpanText(
                text = "Kotlin Mapper",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Constructor",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Extension Functions",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Mapper interface",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "More than one class mapping iterator",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Explicit lambda version",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = ":: Method reference shorthand",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/mccostic/NBRideMultiPlatform.git (Explicit lambda version)
            //https://github.com/Cairo-Squad/EvolveFit.git
            //https://github.com/xephosbot/AnilibriaRefresh.git (Method reference shorthand)
            KotlinCode(
                code = """
                    //Koin 
                    
                    @kotlinx.serialization.Serializable
                    data class LoginResponse(
                        val accessToken: String,
                        val email: String,
                        val firstName: String
                    )
                    
                    data class UserDomainModel(
                        val accessToken: String?=null,
                        val email: String?=null,
                        val firstName: String?=null
                    )
                    
                    class LoginResponseToDomainMapper : Mapper<LoginResponse, UserDomainModel> {
                        override fun map(input: LoginResponse): UserDomainModel =
                            UserDomainModel(
                                accessToken = input.accessToken,
                                email = input.email,
                                firstName = input.firstName
                            )
                    }
                    
                    class UserDomainToUiMapper : Mapper<UserDomainModel, UserUiModel> {
                        override fun map(input: UserDomainModel): UserUiModel =
                            UserUiModel(
                                email = input.email,
                                firstName = input.firstName
                            )
                    }
                    
                    interface Mapper<I, O> {
                        fun map(input: I): O
                    }
                    
                    class AuthApiImp(private val http: HttpClient, private val base: String, private val mapper: LoginResponseToDomainMapper):AuthApi {
                        override suspend fun login(email: String, password: String): UserDomainModel {
                            val dto: LoginResponse = http.post("") {
                                contentType(ContentType.Application.Json)
                                setBody(LoginRequest(email, password))
                            }.body()
                            return mapper.map(dto)
                        }
                    }
                    
                    val loginMappersModule = module {
                        single { LoginResponseToDomainMapper() }
                        single<Mapper<UserDomainModel, UserUiModel>> { UserDomainToUiMapper() }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //core
                    @Serializable
                    data class Result(
                        val background_image: String,
                        val id: Int,
                        val name: String,
                    )
                    
                    //domain
                    data class Game(
                        val id : Int,
                        val name : String,
                        val imageUrl : String
                    )

                    //mapping
                    fun List<Result>.toDomainListOfGames(): List<Game> = map {
                        Game(
                            id = it.id,
                            it.name,
                            imageUrl = it.background_image
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //domain
                    data class Product(
                        val id: Int,
                        val title: String,
                        val price: BigDecimal,
                        val category: String,
                        val description: String,
                        val image: String,
                        val rating: Rating,
                    ) {
                        data class Rating (
                            val rate: Float,
                            val count: Int
                        )
                    }

                    //data
                    data class ProductEntity(
                        val id: Int,
                        val title: String?,
                        val price: Double?,
                        val category: String?,
                        val description: String?,
                        val image: String?,
                        val rating: Rating?
                    ) {
                        data class Rating(
                            val rate: Float?,
                            val count: Int?
                        )
                    }

                   //mapping
                    class ProductMapper {
                        companion object {
                            fun toDomain(pe: ProductEntity): Product = Product(
                                pe.id,
                                pe.title.orEmpty(),
                                BigDecimal(pe.price ?: 0.0).setScale(2, RoundingMode.HALF_UP) ?: BigDecimal.ZERO,
                                pe.category.orEmpty().replaceFirstChar {
                                    if (it.isLowerCase()) it.titlecase() else it.toString()
                                },
                                pe.description.orEmpty(),
                                pe.image.orEmpty(),
                                Product.Rating(pe.rating?.rate.orZero(), pe.rating?.count.orZero())
                            )
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class Category(
                        val name: String,
                        val color: Int = Ivy.toArgb(),
                        val icon: String? = null,
                        val orderNum: Double = 0.0,

                        val isSynced: Boolean = false,
                        val isDeleted: Boolean = false,

                        val id: UUID = UUID.randomUUID()
                    ) {
                        fun toEntity(): CategoryEntity = CategoryEntity(
                            name = name,
                            color = color,
                            icon = icon,
                            orderNum = orderNum,
                            isSynced = isSynced,
                            isDeleted = isDeleted,
                            id = id
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Serializable
                    internal data class MovieRemote(
                        val id: Int,
                        val title: String,
                        val overview: String,
                        @SerialName("poster_path")
                        val posterImage: String,
                        @SerialName("release_date")
                        val releaseDate: String
                    )

                    import kotlinx.serialization.SerialName
                    import kotlinx.serialization.Serializable

                    @Serializable
                    internal data class MovieRemote(
                        val id: Int,
                        val title: String,
                        val overview: String,
                        @SerialName("poster_path")
                        val posterImage: String,
                        @SerialName("release_date")
                        val releaseDate: String
                    )

                    internal fun MovieRemote.toMovie() : Movie {
                        return Movie(
                            id = id,
                            title = title,
                            description = overview,
                            imageUrl = getImageUrl(posterImage),
                            releaseDate = releaseDate
                        )
                    }

                    private fun getImageUrl(posterImage: String) = ""

                    interface MovieRepository {
                        suspend fun getMovies(page: Int) : List<Movie>
                        suspend fun getMovie(movieId: Int) : Movie
                    }

                    internal class MovieRepositoryImpl(
                        private val remoteDataSource: RemoteDataSource
                    ) : MovieRepository {
                        override suspend fun getMovies(page: Int): List<Movie> {
                            return remoteDataSource.getMovies(page = page).results.map {
                                it.toMovie()
                            }
                        }

                        override suspend fun getMovie(movieId: Int): Movie {
                            return remoteDataSource.getMovie(movieId = movieId).toMovie()
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //List Mapper
                    //domain (ProductEntity.kt)
                    data class Products(
                        var productlist: List<Product>
                    )

                    data class Product (
                        var id: Int=0,
                        var title: String="",
                        val description: String="",
                        val price: Double=0.0,
                        val discountPercentage: Double=0.0,
                        val category: String="",
                        val thumbnail: String=""
                    )

                    //model (ProductDto.kt)
                    @Serializable
                    data class ProductsDto (
                        @SerialName("products")
                       var productlist: List<ProductDto>
                    )

                    @Serializable
                    data class ProductDto (
                        @SerialName("id")
                        var id: Int=0,
                        @SerialName("title")
                        var title: String="",
                        @SerialName("description")
                        val description: String="",
                        @SerialName("price")
                        val price: Double=0.0,
                    )

                    //mapper (ProductMapper.kt)
                    fun ProductsDto.toEntity(): Products {
                        return Products(
                            productlist = productlist.toEntity()
                        )
                    }

                    fun List<ProductDto>.toEntity() = map { it.toEntity() }

                    fun ProductDto.toEntity() = Product(
                        id = id ,
                         title = title,
                        description = description,
                        price = price,
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Run using DTOFromModel with enum

                    //domain
                    data class BingoRoom(
                        val id: String,
                        val hostId: String,
                        val type: BingoType,
                        val name: String,
                        val themeId: String?,
                        val maxWinners: Int,
                        val locked: Boolean,
                        val password: String?,
                        val drawnCharactersIds: List<String>,
                        val state: RoomState,
                        val players: List<String>,
                        val winners: List<String>
                    )

                    enum class BingoType {
                        CLASSIC, THEMED
                    }

                    enum class RoomState {
                        NOT_STARTED, RUNNING, FINISHED
                    }

                    //data
                    @Serializable
                    class BingoRoomDTO(
                        private val id: String,
                        private val hostId: String,
                        private val type: String,
                        private val name: String,
                        private val themeId: String?,
                        private val maxWinners: Int,
                        private val locked: Boolean,
                        private val password: String?,
                        private val drawnCharactersIds: List<String>,
                        private val state: String,
                        private val players: List<String>,
                        private val winners: List<String>
                    ) {
                        fun toModel(): BingoRoom {
                            val modelType = when (type) {
                            "CLASSIC" -> BingoType.CLASSIC
                            else -> BingoType.THEMED
                        }

                        val modelState = when (state) {
                            "NOT_STARTED" -> RoomState.NOT_STARTED
                            "RUNNING" -> RoomState.RUNNING
                            else -> RoomState.FINISHED
                        }

                        return BingoRoom(
                            id = id,
                            hostId = hostId,
                            type = modelType,
                            name = name,
                            themeId = themeId,
                            maxWinners = maxWinners,
                            locked = locked,
                            password = password,
                            drawnCharactersIds = drawnCharactersIds,
                            state = modelState,
                            winners = winners,
                            players = players
                        )
                    }
                }

                fun bingoRoomDTOFromModel(room: BingoRoom): BingoRoomDTO =
                    room.run {
                        BingoRoomDTO(
                            id = id,
                            hostId = hostId,
                            type = type.name,
                            name = name,
                            themeId = themeId,
                            maxWinners = maxWinners,
                            locked = locked,
                            password = password,
                            drawnCharactersIds = drawnCharactersIds,
                            state = state.name,
                            winners = winners,
                            players = players
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlinx.serialization.Serializable

                    @Serializable
                    data class PokemonDTO(
                        val name: String,
                        val url: String = String(),
                        val id: Int? = null,
                        val types: List<PokemonTypeDTO> = emptyList(),
                        val stats: List<PokemonStatsDTO> = emptyList()
                    ) {

                        val index: Int
                            get() = id ?: url.split("/".toRegex()).dropLast(1).last().toInt()

                        val imageUrl: String
                            get() {
                                return StringBuilder()
                                    .append("https://raw.githubusercontent.com")
                                    .append("/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork")
                                    .append("/$") //index.png
                                    .toString()
                            }
                    }


                    @Serializable
                    data class PokemonTypeDTO(
                        val type: Type
                    ) {
                        @Serializable
                        data class Type(
                            val name: String
                        )
                    }

                    @Serializable
                    data class PokemonStatsDTO(
                        @SerialName("base_stat")
                        val baseStat: Int,
                        val stat: PokemonStatDTO
                    ) {

                        @Serializable
                        data class PokemonStatDTO(
                            val name: String,
                        )
                    }

                    class PokemonMapper {

                        fun toDomain(pokemons: List<PokemonDTO>): List<Pokemon> = pokemons.map(::toDomain)

                        fun toDomain(pokemon: PokemonDTO) = Pokemon(
                            id = pokemon.index,
                            name = pokemon.name,
                            types = pokemon.types.map { toDomain(it) },
                            imageUrl = pokemon.imageUrl,
                            stats = pokemon.stats.mapNotNull {
                                toDomainStats(it) { stat -> !StatType.isSpecial(stat) }
                            },
                            specialStats = pokemon.stats.mapNotNull { toDomainStats(it, StatType::isSpecial) }
                        )

                        private fun toDomain(type: PokemonTypeDTO) = PokemonType(name = type.type.name)

                        private fun toDomainStats(stats: PokemonStatsDTO, predicate: (String) -> Boolean) = when {
                            predicate(stats.stat.name) -> Pokemon.Stats(getShortName(stats.stat.name), stats.baseStat)
                            else -> null
                        }
                    }

                    enum class StatType(
                        val statName: String,
                        val shortName: String,
                        val isSpecial: Boolean = false,
                    ) {
                        HP("HP", "HP"),
                        ATTACK("attack", "ATK"),
                        DEFENSE("defense", "DEF"),
                        SPEED("speed", "SPD"),
                        SPECIAL_ATTACK("special-attack", "SPD-ATK", true),
                        SPECIAL_DEFENSE("special-defense", "SPD-DEF", true);

                        companion object {
                            fun isSpecial(name: String) = StatType.entries.find {
                                it.statName.contains(name, true)
                            }?.isSpecial ?: false

                            fun getShortName(name: String) = StatType.entries.find {
                                it.statName.contains(name, true)
                            }?.shortName ?: name
                        }
                    }

                    class PokemonDetailViewModel(
                        savedStateHandle: SavedStateHandle,
                        private val repository: PokemonRepository
                    ) : ViewModel() {

                        @OptIn(ExperimentalCoroutinesApi::class)
                        val uiState = savedStateHandle.getStateFlow(NAME_ARG, "")
                            .flatMapConcat { name ->
                                require(name.isNotBlank()) { "Pokemon name must be passed" }
                                repository.getPokemon(name)
                            }
                            .map { pokemon -> PokemonDetailUiState(pokemon = pokemon, isLoading = false) }
                            .catch { exception ->
                                // TODO("Handle error, not implemented yet")
                                throw exception
                            }
                            .stateIn(
                                viewModelScope,
                                SharingStarted.Eagerly,
                                PokemonDetailUiState()
                            )
                    }

                    fun getPokemon(name: String): Flow<Pokemon> = pokemonService.fetchPokemon(name)
                                                                  .map(pokemonMapper::toDomain)
                                                                  .flowOn(dispatcher)

                     object AppModule {
                        private val dataModules = module {
                            singleOf(::PokemonMapper)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //https://github.com/salimisler28/ComposeMultiplatformMVVM

                    import kotlinx.serialization.Serializable

                    @Serializable
                    data class Resource<T>(
                        val status: String,
                        val data: T?,
                        val message: String?
                    ) {
                        val isSuccess get() = status == STATUS_SUCCESS

                        fun <A> map(func: (T?) -> A): Resource<A> {
                            return Resource(status = status, data = func.invoke(data), message = message)
                        }

                        companion object {
                            private const val STATUS_SUCCESS = "Success"
                            private const val STATUS_ERROR = "Error"

                            fun <T> success(data: T): Resource<T> {
                                return Resource(status = STATUS_SUCCESS, data = data, message = null)
                            }

                            fun <T> error(error: Exception?): Resource<T> {
                                return Resource(status = STATUS_ERROR, data = null, message = error?.message)
                            }
                        }
                    }

                    import io.ktor.client.call.body
                    import io.ktor.client.statement.HttpResponse
                    import io.ktor.http.isSuccess

                    suspend inline fun <reified T> makeCall(
                        call: () -> HttpResponse
                    ): Resource<T> {
                        return try {
                            val response = call.invoke()
                            return if (response.status.isSuccess()) {
                                Resource.success(response.body<T>())
                            } else {
                                Resource.error(Exception(response.status.toString()))
                            }
                        } catch (e: Exception) {
                            Resource.error(e)
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/bardss/MultiPokeQL
            KotlinCode(
                code = """
                    sealed interface DataResult<T> {
                        data class Success<T>(val data: T) : DataResult<T>
                        data class Error<T>(val message: String? = null) : DataResult<T>
                    }

                    fun <T> ServiceResponse<T>.toDataResult(): DataResult<T> {
                        return when (this) {
                            is ServiceResponse.Success -> DataResult.Success(data)
                            is ServiceResponse.Error -> DataResult.Error(message)
                        }
                    }
                    fun <T, Y> DataResult<T>.mapSuccess(map: (T) -> Y): DataResult<Y> {
                        return when (this) {
                            is DataResult.Success -> DataResult.Success(map(data))
                            is DataResult.Error -> DataResult.Error(message)
                        }
                    }

                    sealed interface ServiceResponse<T> {
                        data class Success<T>(val data: T) : ServiceResponse<T>
                        data class Error<T>(val message: String? = null) : ServiceResponse<T>
                    }
                    """.trimIndent()
            )

            //https://medium.com/@himanshugaur684/stop-using-extension-functions-for-domain-mapping-in-clean-architecture-c345cb5a633c
            SpanText(
                text = "Stop Using Extension Functions for Domain Mapping in Clean Architecture - (Use explicit mapper classes or interfaces)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    interface UserDtoMapper {
                        fun toDomain(dto: UserDto): User
                        fun toDto(user: User): UserDto
                    }

                    class UserDtoMapperImpl : UserDtoMapper {
                        override fun toDomain(dto: UserDto): User {
                            return User(
                                id = UserId(dto.id),
                                name = dto.name,
                                email = Email(dto.email)
                            )
                        }

                        override fun toDto(user: User): UserDto {
                            return UserDto(
                                id = user.id.value,
                                name = user.name,
                                email = user.email.value
                            )
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "viewmodel private mutableState set",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    sealed class RunStatus<T>(
                        val data: T? = null,
                        val message: String? = null
                    ) {
                        class Idle<T> : RunStatus<T>()
                        class Success<T>(data: T) : RunStatus<T>(data = data)
                        class Error<T>(errorMessage: String) : RunStatus<T>(message = errorMessage)
                        class Loading<T> : RunStatus<T>()
                    }
                    
                    import androidx.lifecycle.ViewModel
                    import androidx.compose.runtime.* 

                    class AddAreaViewModel(
                    ): ViewModel() {

                        //1
                        var addState by mutableStateOf<RunStatus<ByteArray?>>(RunStatus.Idle())
                            private set
                           
                        //2. semicolon    
                        //Reduce code and then the compiler will know and understand our logic here
                        var addState by mutableStateOf<RunStatus<ByteArray?>>(RunStatus.Idle()); private set 
                           
                        //3   
                        var username by mutableStateOf("")
                           private set   
                         
                        //4
                        var accessToken: String? = null
                           private set
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class LineupListState(
                        val stages: List<Stage> = emptyList(),
                    )

                    data class Stage(
                        val title: String,
                        val expanded: Boolean,
                        val items: List<Pair<String, String>>,
                    ) {
                        fun getContainerColor(): Color {
                            return when (title) {
                                "Stage A" -> LineupListColors.lime
                                "Stage B" -> LineupListColors.orange
                                "Stage C" -> LineupListColors.pink
                                else -> LineupListColors.lime
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Request State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    sealed class RequestState<out T> {
                        data object Idle : RequestState<Nothing>()
                        data object Loading : RequestState<Nothing>()
                        data class Success<out T>(val data: T) : RequestState<T>()
                        data class Error(val message: String) : RequestState<Nothing>()

                        fun isIdle(): Boolean = this is Idle
                        fun isLoading(): Boolean = this is Loading
                        fun isError(): Boolean = this is Error
                        fun isSuccess(): Boolean = this is Success
                        fun getSuccessData() = (this as Success).data
                        fun getSuccessDataOrNull() = if (this.isSuccess()) this.getSuccessData() else null
                        fun getErrorMessage(): String = (this as Error).message
                    }

                    @Composable
                    fun <T> RequestState<T>.DisplayResult(
                        modifier: Modifier = Modifier,
                        onIdle: (@Composable () -> Unit)? = null,
                        onLoading: (@Composable () -> Unit)? = null,
                        onError: (@Composable (String) -> Unit)? = null,
                        onSuccess: @Composable (T) -> Unit,
                        transitionSpec: ContentTransform? = scaleIn(tween(durationMillis = 400))
                                + fadeIn(tween(durationMillis = 800))
                                togetherWith scaleOut(tween(durationMillis = 400))
                                + fadeOut(
                            tween(durationMillis = 800)
                        ),
                        backgroundColor: Color? = null
                    ) {
                        AnimatedContent(
                            modifier = modifier
                                .background(color = backgroundColor ?: Color.Unspecified),
                            targetState = this,
                            transitionSpec = {
                                transitionSpec ?: (EnterTransition.None togetherWith ExitTransition.None)
                            },
                            label = "Content Animation"
                        ) { state ->
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.Center
                            ) {
                                when (state) {
                                    is RequestState.Idle -> {
                                        onIdle?.invoke()
                                    }

                                    is RequestState.Loading -> {
                                        onLoading?.invoke()
                                    }

                                    is RequestState.Error -> {
                                        onError?.invoke(state.getErrorMessage())
                                    }

                                    is RequestState.Success -> {
                                        onSuccess(state.getSuccessData())
                                    }
                                }
                            }
                        }
                    }
                    
                    //ViewModel
                    class LoginViewModel : ViewModel(), KoinComponent {
                        private val _advert = MutableStateFlow<RequestState<AdvertGetResponse.Advert>>(RequestState.Loading)
                        val advert: StateFlow<RequestState<AdvertGetResponse.Advert>> = _advert.asStateFlow()
                        
                        //OR
                        var advert by mutableStateOf(RequestState<AdvertGetResponse.Advert>(RequestState.Loading))
                            private set
                            
                        //OR 
                        //Reduce code and then the compiler will know and understand our logic here
                        var advert by mutableStateOf(RequestState<AdvertGetResponse.Advert>(RequestState.Loading)); private set
                            
                        //_advert.value = RequestState.Loading
                        //_advert.value = RequestState.Success()
                        //_advert.value = RequestState.Error()
                        
                        //+ Convert to State
                        data class PetList(
                            val pets: List<Pet>
                        )

                        data class Pet()
                        
                        var uiState by mutableStateOf(PetListScreenState())
                            private set
                            
                        uiState = uiState.copy(
                            pets = uiState.pets + response.pets,
                            pageData = response.pageData,
                            isLoading = false
                        )    
                    }
                        
                    //UI
                    advert.DisplayResult(
                        onLoading = {
                        
                        },
                        onSuccess = { advertData ->
                        
                        },
                        onError = { message ->
                       
                        
                        }
                    )  
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Good Tips 
                    
                    private val updatesFlow = useCase.updateFlow()
                        .shareIn(
                            scope = viewModelScope, 
                            started = SharingStarted.WhileSubscribed(),
                        )
                        
                    fun updatesFlow() =  updatesFlow   
                    """.trimIndent()
            )

            //2. https://github.com/droidwithme/product-catalog.git
            KotlinCode(
                code = """
                    public sealed interface Result<out T> {
                        public data class Success<T>(val value: T) : Result<T>
                        public data class Error(val exception: Throwable) : Result<Nothing>
                        public data object Loading : Result<Nothing>
                    }

                    public fun <T> Flow<T>.asResult(): Flow<Result<T>> =
                        map<T, Result<T>> { value -> Result.Success(value = value) }
                            .onStart { emit(Result.Loading) }
                            .catch { exception -> emit(Result.Error(exception = exception)) }
                    
                    @OptIn(kotlinx.coroutines.FlowPreview::class)
                    @Stable
                    internal class HomeViewModel(
                        private val getProductsUseCase: GetProductsUseCase,
                        private val searchProductsUseCase: SearchProductsUseCase
                    ) : ViewModel() {

                        private val _error = MutableStateFlow<Throwable?>(null)
                        private val _products = MutableStateFlow<List<Products>>(emptyList())
                        private val _searchQuery = MutableStateFlow("")
                        private val _searchActive = MutableStateFlow(false)

                        private var currentPage = 0
                        private var totalPages = 1
                        private var isFetching = false

                        @OptIn(ExperimentalCoroutinesApi::class)
                        private val searchResults = _searchQuery
                            .debounce(300)
                            .flatMapLatest { query ->
                                if (query.isNotBlank()) {
                                    searchProductsUseCase(SearchProductsUseCase.Param(query))
                                } else {
                                    MutableStateFlow(Result.Success(com.revest.demo.shared.domain.model.PaginatedData(0, emptyList(), 0, 0)))
                                }
                            }
                            .stateIn(viewModelScope, WhileUiSubscribed, Result.Loading)

                        val error: StateFlow<Throwable?> = _error
                        val uiState: StateFlow<HomeViewState> = combine(
                            _products,
                            searchResults,
                            _searchQuery,
                            _searchActive,
                            error
                        ) { products, searchResults, query, active, error ->
                            HomeViewState(
                                productsViewState = products.toViewState(),
                                searchResults = searchResults.toViewStatePaginated(),
                                searchQuery = query,
                                searchActive = active,
                                error = error
                            )
                        }.stateIn(
                            scope = viewModelScope,
                            started = WhileUiSubscribed,
                            initialValue = HomeViewState(),
                        )

                        init {
                            loadMore()
                        }

                        fun onSearchQueryChange(query: String) {
                            _searchQuery.value = query
                        }

                        fun onSearchActiveChange(active: Boolean) {
                            _searchActive.value = active
                        }

                        fun loadMore() {
                            if (isFetching || currentPage >= totalPages) return

                            viewModelScope.launch {
                                isFetching = true
                                getProductsUseCase(param = GetProductsUseCase.Param(limit = 10, skip = currentPage * 10))
                                    .collect { result ->
                                        when (result) {
                                            is Result.Success -> {
                                                val response = result.value
                                                if (response.data.isNotEmpty()) {
                                                    _products.value += response.data
                                                    totalPages = response.totalPages
                                                    currentPage++
                                                }
                                            }
                                            is Result.Error -> {
                                                _error.value = result.exception
                                            }
                                            Result.Loading -> { /* No-op */ }
                                        }
                                        isFetching = false
                                    }
                            }
                        }

                        fun onError(error: Throwable) {
                            _error.tryEmit(value = error)
                        }

                        fun onErrorConsumed() {
                            _error.tryEmit(value = null)
                        }
                    }       
                    """.trimIndent()
            )

            //1. https://github.com/PiDyGB/CMPMyNASADailyPics
            KotlinCode(
                code = """
                    //Result State with Test Cases
                    //1
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.catch
                    import kotlinx.coroutines.flow.map
                    import kotlinx.coroutines.flow.onStart
                    
                    sealed interface Result<out T> {
                        data class Success<T>(val data: T) : Result<T>
                        data class Error(val exception: Throwable) : Result<Nothing>
                        data object Loading : Result<Nothing>
                    }
                    
                    fun <T> Flow<T>.asResult(): Flow<Result<T>> = map<T, Result<T>> { Result.Success(it) }
                    .onStart { emit(Result.Loading) }
                    .catch { emit(Result.Error(it)) }
                    
                    val pictures = repository.pictures.onStart {
                        repository.getPictures()
                    }.asResult().stateIn(
                        scope = viewModelScope,
                        started = SharingStarted.WhileSubscribed(5_000),
                        initialValue = Result.Loading
                    )
                    
                    //Test Cases
                    import kotlinx.coroutines.flow.flow
                    import kotlinx.coroutines.flow.toList
                    import kotlinx.coroutines.test.runTest
                    import kotlin.test.Test
                    import kotlin.test.assertEquals
                    import kotlin.test.assertIs

                    class ResultTest {
                        @Test
                        fun `asResult emits Loading then Success for successful flow`() = runTest {
                            val flow = flow { emit("data") }
                            val results = flow.asResult().toList()

                            assertEquals(2, results.size)
                            assertIs<Result.Loading>(results[0])
                            assertIs<Result.Success<String>>(results[1])
                            assertEquals("data", (results[1] as Result.Success<String>).data)
                        }

                        @Test
                        fun `asResult emits Loading then Error for failing flow`() = runTest {
                            val exception = RuntimeException("Test exception")
                            val flow = flow<String> { throw exception }
                            val results = flow.asResult().toList()

                            assertEquals(2, results.size)
                            assertIs<Result.Loading>(results[0])
                            assertIs<Result.Error>(results[1])
                            assertEquals(exception, (results[1] as Result.Error).exception)
                        }

                        @Test
                        fun `asResult emits Loading then completes for empty flow`() = runTest {
                            val flow = flow<String> {}
                            val results = flow.asResult().toList()

                            assertEquals(1, results.size)
                            assertIs<Result.Loading>(results[0])
                        }
                    }
                """.trimIndent()
            )

            SpanText(
                text = "Result Handle kotlinx.serialization JsonElement",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/music-assistant/mobile-app.git
            KotlinCode(
                code = """
                    data class Answer(
                        val json: JsonObject
                    ) {
                        val messageId: String? = json["message_id"]?.jsonPrimitive?.content
                        val result: JsonElement? = json["result"]
                        
                        inline fun <reified T : Any> resultAs(): T? = result?.let { myJson.decodeFromJsonElement(it) }
                    }
                    
                    import kotlinx.serialization.json.Json
                    val myJson = Json {
                        prettyPrint = true
                        encodeDefaults = true
                        ignoreUnknownKeys = true
                    }
                    
                    inline fun <reified T : Any> Result<Answer>.resultAs(): T? = getOrNull()?.resultAs()
                    
                    @Serializable
                    data class ServerMediaItem()
                    
                    //API Get Result
                    result.getOrNull()?.resultAs<SearchResult>()?.ItemListMapper()
                    """.trimIndent()
            )

            SpanText(
                text = "Scalable ViewModel State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    data class ProductListScreenState(
                        val fullName: String,
                        val displayState: DisplayState? = null
                    )

                    sealed interface DisplayState {
                        data object Loading : DisplayState
                        data object Error : DisplayState
                        data class Content(
                            val productResults: ProductResults,
                            val contentDisplayState: ContentDisplayState? = null
                        ) : DisplayState
                    }

                    sealed interface ContentDisplayState {
                        data object Refreshing : ContentDisplayState
                        data object Paginating : ContentDisplayState
                        data object PaginationError : ContentDisplayState
                    }
                    """.trimIndent()
            )

            //https://github.com/Ehsan-Ullah-Tariq/CMP_KMP_Boilerplate
            //invoke() operator function within a class allows its instances to be called as functions.
            KotlinCode(
                code = """
                    sealed class UiStatesResource<out T> {
                        object Loading : UiStatesResource<Nothing>()
                        data class Success<out T> (val value: T) : UiStatesResource<T>()
                        data class Error(val message: String?) : UiStatesResource<Nothing>()
                    }

                    sealed class ApiResultResource<out T> {
                        object Loading : ApiResultResource<Nothing>()
                        data class Success<out T> (val value: T) : ApiResultResource<T>()
                        data class Error(val message: String?) : ApiResultResource<Nothing>()
                    }

                    class HomeViewModel(private val getCoinsUseCase: GetCoinsUseCase) : ViewModel() {
                        private var currentPage = 1
                        private val pageSize = 50
                        private val _homeFeedUiState = MutableStateFlow<UiStatesResource<List<CoinsDomainItem>>>(UiStatesResource.Loading)
                        val homeFeedUiState: StateFlow<UiStatesResource<List<CoinsDomainItem>>> = _homeFeedUiState.stateIn(
                            scope = viewModelScope,
                            started = SharingStarted.WhileSubscribed(5000),
                            initialValue = UiStatesResource.Loading
                        )

                        fun fetchHomeData() = viewModelScope.launch {
                            getCoinsUseCase(currentPage, pageSize).collect { result ->
                                when (result) {
                                    is ApiResultResource.Error -> {

                                    }

                                    ApiResultResource.Loading -> {
                                        _homeFeedUiState.value = UiStatesResource.Loading
                                    }

                                    is ApiResultResource.Success -> {

                                    }
                                }
                            }
                        }
                    }

                    @Composable
                    fun HomeScreen(viewModel: HomeViewModel = koinViewModel()) {
                        Column(
                            modifier = Modifier.fillMaxSize(),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            val homeData by viewModel.homeFeedUiState.collectAsStateWithLifecycle()
                            LaunchedEffect(Unit) {
                                if (homeData !is UiStatesResource.Success) viewModel.fetchHomeData()
                            }
                            SetCoinList(homeData)
                        }
                    }

                    @Composable
                    private fun SetCoinList(homeData: UiStatesResource<List<CoinsDomainItem>>) {
                        Column(
                            modifier = Modifier.fillMaxSize(),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            when (homeData) {
                                is UiStatesResource.Loading -> {
                                    GradientCircularProgressIndicator()
                                }

                                is UiStatesResource.Success -> {

                                }

                                is UiStatesResource.Error -> {

                                }
                            }
                        }
                    }

                    @Composable
                    fun GradientCircularProgressIndicator(
                        modifier: Modifier = Modifier,
                        size: Dp = 48.dp,
                        strokeWidth: Dp = 6.dp,
                        gradientColors: List<Color> = listOf(Color(0xFF00C6FF), Color(0xFF0072FF)) // Blue gradient
                    ) {
                            val infiniteTransition = rememberInfiniteTransition(label = "LoadingAnim")
                            val angleOffset = infiniteTransition.animateFloat(
                                initialValue = 0f,
                                targetValue = 360f,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(1000, easing = LinearEasing),
                                    repeatMode = RepeatMode.Restart
                                ),
                                label = "RotationAngle"
                            )

                            Box(
                                modifier = Modifier
                                    .size(100.dp)
                                    .background(Color.Gray.copy(alpha = 0.3f), shape = RoundedCornerShape(20.dp)), // Optional dim background
                                contentAlignment = Alignment.Center
                            ) {
                                Canvas(modifier = modifier.size(size)) {
                                    val diameter = size.toPx()
                                    val stroke = strokeWidth.toPx()
                                    drawArc(
                                        brush = Brush.sweepGradient(gradientColors),
                                        startAngle = angleOffset.value,
                                        sweepAngle = 270f,
                                        useCenter = false,
                                        size = Size(diameter, diameter),
                                        topLeft = Offset(x = 0f, y = 0f),
                                        style = Stroke(
                                            width = stroke
                                        )
                                    )
                                }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/GasparKral/Fit-me
            KotlinCode(
                code = """
                    sealed class Either<out E, out T> where E : Exception, T : Any {
                        data class Failure<out E : Exception>(val error: E) : Either<E, Nothing>()

                        data class Success<out T : Any>(val value: T) : Either<Nothing, T>()

                        inline fun fold(onSuccess: (T) -> Unit, onFailure: (E) -> Unit) {
                            when (this) {
                                is Failure -> onFailure(error)
                                is Success -> onSuccess(value)
                            }
                        }

                        val isFailure: Boolean get() = this is Failure<E>
                        val isSuccess: Boolean get() = this is Success<T>
                    }

                    abstract class UseCase<out Type, in Params> where Type : Any {

                        abstract suspend fun run(params: Params): Either<Exception, Type>

                        suspend operator fun invoke(params: Params, onSuccess: (Type) -> Unit, onFailure: (Exception) -> Unit) {
                            run(params).fold(
                                onSuccess,
                                onFailure
                            )
                        }

                        object None
                    }

                    abstract class API<ApiType> where ApiType : Any {

                        protected abstract val apiPath: String

                        protected val httpClient = HttpClient(CIO) {
                            install(ContentNegotiation) {
                                json(Json { prettyPrint = true; isLenient = true; ignoreUnknownKeys = true })
                            }
                        }

                        operator fun invoke() {
                            this.httpClient.close()
                        }

                        abstract suspend fun post(
                            route: String = apiPath,
                            body: ApiType
                        ): Either<Exception, ApiType>

                        abstract suspend fun get(route: String = apiPath, vararg params: String?): Either<Exception, ApiType>

                        abstract suspend fun getList(route: String = apiPath, vararg params: String?): Either<Exception, List<ApiType>>

                        abstract suspend fun delete(route: String = apiPath, vararg params: String?): Either.Failure<Exception>?

                        abstract suspend fun patch(route: String = apiPath, body: Any): Either.Failure<Exception>?
                    }

                    class DeleteTrainerAccount() : UseCase<UseCase.None, Trainer>() {
                        override suspend fun run(params: Trainer): Either<Exception, None> {

                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/NatashaIlievska/Pokemon-CMP
            KotlinCode(
                code = """
                    //ResultExtensions.kt
                    fun <T> Result<T>.getFailureValue() = this.exceptionOrNull()?.message

                    fun <T> Result<T>.getThrowable(): Throwable = exceptionOrNull() ?: Exception()

                    fun <T> Result<T>.toUIState(): UiState<T> = fold(
                        onSuccess = {
                            UiState.Success(it)
                        },
                        onFailure = {
                            UiState.Error(this.getFailureValue())
                        }
                    )

                    //UIState.kt
                    sealed class UiState<out R> {
                        data object Loading : UiState<Nothing>()
                        data class Success<out T>(val data: T?) : UiState<T>()
                        data class Error(val message: String?) : UiState<Nothing>()

                        fun getSuccessData() = (this as? Success)?.data
                    }

                    //ErrorView.kt
                    @Composable
                    fun ErrorView(errorMessage: String, modifier: Modifier = Modifier, onRetryPress: (() -> Unit)) {
                        Column(
                            modifier = modifier.fillMaxWidth(),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                text = errorMessage,
                                modifier = Modifier
                                    .padding(top = 20.dp),
                                textAlign = TextAlign.Center,
                                style = MaterialTheme.typography.labelMedium
                            )

                            Button(
                                onClick = { onRetryPress.invoke() },
                                modifier = Modifier
                                    .padding(top = 20.dp)
                            ) {
                                Text(
                                    text = stringResource(Res.string.try_again)
                                )
                            }
                        }
                    }

                    //UIStateContent.kt
                    @Composable
                    fun <T> UiStateContent(
                        uiState: UiState<T>,
                        retryCallback: () -> Unit,
                        modifier: Modifier = Modifier,
                        loadingContent: @Composable () -> Unit = {
                            FullScreenLoading()
                        },
                        failureContent: @Composable (String?) -> Unit = {
                            Column(
                                 modifier = Modifier
                                    .fillMaxSize()
                                    .padding(
                                        horizontal = 24.dp,
                                        vertical = 20.dp
                                    )
                                    .verticalScroll(rememberScrollState())
                            ) {
                                ErrorView(
                                    errorMessage = it ?: stringResource(Res.string.something_went_wrong),
                                    onRetryPress = retryCallback
                                )
                            }
                        },
                        successContent: @Composable (T) -> Unit
                    ) {
                        Box(modifier = modifier) {
                            when (uiState) {
                                is UiState.Loading -> {
                                    loadingContent()
                                }
                                is UiState.Error -> {
                                    failureContent(uiState.message)
                                }
                                is UiState.Success -> {
                                    if (uiState.data == null) {
                                        failureContent(stringResource(Res.string.no_data_available))
                                    } else {
                                        successContent(uiState.data)
                                    }
                                }
                            }
                        }
                    }

                    //FullScreenLoading.kt
                    @Composable
                    fun FullScreenLoading(modifier: Modifier = Modifier) {
                        Box(
                            modifier = modifier
                                .fillMaxSize()
                                .wrapContentSize(Alignment.Center)
                        ) {
                            CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                        }
                    }
                """.trimIndent()
            )

            //https://github.com/beHqo/MockStore
            KotlinCode(
                code = $$"""
                    class ProductsViewModel(private val fetchProductsUseCase: FetchProductsUseCase) : ViewModel() {

                        /**
                         * A private state flow that toggles between true/false whenever `refresh()` is called.
                         *
                         * We only care about the emission itself, not its boolean value. Emitting a new value here
                         * (even if it toggles from `false` → `true` or `true` → `false`) will trigger the combined flow
                         * to re-emit the same `category`, which in turn causes `flatMapLatest` to restart
                         * `fetchProductsUseCase(category)`. This is how we force a reload on demand.
                         */
                        private val refresh = MutableStateFlow(false)

                        private val _selectedCategory = MutableStateFlow<ProductCategory?>(null)
                        val selectedCategory: StateFlow<ProductCategory?> = _selectedCategory.asStateFlow()

                        /**
                         * A StateFlow of `Result<List<Product>>` that the UI collects.
                         *
                         * Implementation details:
                         * - We combine `selectedCategory` and `refresh`, the lambda
                         *   returns only the current category; `_` (the refresh value) is discarded.
                         * - Any time `selectedCategory` changes *or* `refresh` emits a new boolean, `combine` emits the current `category`.
                         * - We then call `flatMapLatest { category -> fetchProductsUseCase(category) }`, so:
                         *     • The first time this is collected, `combine` emits `(null)` → `flatMapLatest` calls `fetchProductsUseCase(null)`.
                         *     • When `setCategory(...)` toggles `_selectedCategory`, `combine` emits the new category → `flatMapLatest`
                         *       cancels any in-flight flow and starts `fetchProductsUseCase(newCategory)`.
                         *     • When `refresh()` flips `refresh` from `false` → `true`, `combine` re-emits `(currentCategory)`, so
                         *       `flatMapLatest` restarts `fetchProductsUseCase(currentCategory)` even though the category did not change.
                         * - Finally, we wrap the result in `stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), Result.Loading)`,
                         *   which converts the cold flow into a hot [StateFlow], scoped to this ViewModel’s coroutine scope, with:
                         *     • An initial value of `Result.Loading`.
                         *     • A “started” policy of `WhileSubscribed(5000)` so that it remains active for 5 seconds after the last collector
                         *       is gone before stopping. A hacky but common way of surviving configuration changes in android.
                         */
                        @OptIn(ExperimentalCoroutinesApi::class) //flatMapLatest
                        val products = selectedCategory.combine(refresh) { category, _ -> category }
                            .flatMapLatest { category -> fetchProductsUseCase(category) }
                            .stateIn(viewModelScope, WhileSubscribed(5000), Result.Loading)

                        fun setCategory(category: ProductCategory?) {
                            _selectedCategory.update { if (category == it) null else category }
                        }

                        fun refresh() {
                            refresh.update { !it }
                        }
                    }
                    
                    sealed interface Result<out D> {
                        data class Success<out D>(val data: D) : Result<D>
                        data class Error(val message: String) : Result<Nothing>
                        object Loading : Result<Nothing>
                    }

                    /**
                     * If `this` is a `Success<D>`, apply [transform] to its data and wrap in `Success<R>`.
                     * Otherwise (Loading or Error), return the same instance (typed as `Result<R>`).
                     */
                    inline fun <D, R> Result<D>.map(transform: (D) -> R): Result<R> = when (this) {
                        is Result.Success -> Result.Success(transform(data))
                        is Result.Error -> this
                        Result.Loading -> Result.Loading
                    }
                    
                    private const val TAG = "ProductsApi"
                    private const val PRODUCTS_ENDPOINT = ""
                    private const val CATEGORY_ENDPOINT = ""

                    class ProductsApi(
                        private val httpClient: HttpClient, private val ioDispatcher: CoroutineDispatcher
                    ) : IProductsApi {
                        override suspend fun fetchProducts(
                            category: ProductCategory?
                        ): Result<List<Product>> = withContext(ioDispatcher) {
                            val endpoint =
                                if (category == null) PRODUCTS_ENDPOINT else "$CATEGORY_ENDPOINT${category.rawName}"

                            return@withContext makeHttpRequestSafely<List<ProductDTO>>(TAG) { httpClient.get(endpoint) }
                                .map { list -> list.map { productDTO -> productDTO.toDomainModel() } }
                        }
                    }
                    
                    const val MAIN_DISPATCHER = "MainDispatcher"
                    const val DEFAULT_DISPATCHER = "DefaultDispatcher"
                    const val IO_DISPATCHER = "IODispatcher"

                    val dispatchersModule = module {
                        factory(named(MAIN_DISPATCHER)) { Dispatchers.Main }
                        factory(named(DEFAULT_DISPATCHER)) { Dispatchers.Default }
                        factory(named(IO_DISPATCHER)) { Dispatchers.IO }
                    }
                    
                    val productsModule = module {
                        single<IProductsApi> {
                            ProductsApi(httpClient = get(), ioDispatcher = get(named(IO_DISPATCHER)))
                        }
                    }
                    
                    suspend inline fun <reified T> makeHttpRequestSafely(
                        logTag: String, noinline block: suspend () -> HttpResponse
                    ): Result<T> = try {
                        val response = block()

                        Log.i(logTag, "response status code: ${response.status}")

                        Result.Success(response.body<T>())
                    } catch (e: ResponseException) {
                        Log.e(logTag, "Failed to make http request", e)

                        Result.Error(e.message ?: "Failed to make http request due to an unknown error")
                    } catch (e: HttpRequestTimeoutException) {
                        Log.e(logTag, "HTTP request timed out has exceeded", e)

                        Result.Error("HTTP request timed out has exceeded")
                    } catch (e: ConnectTimeoutException) {
                        Log.e(logTag, "Connection timed out has exceeded", e)

                        Result.Error("Connection timed out has exceeded")
                    } catch (e: SocketTimeoutException) {
                        Log.e(logTag, "Socket timed out has exceeded", e)

                        Result.Error("Socket timed out has exceeded")
                    } catch (e: SerializationException) {
                        Log.e(logTag, "Failed to deserialize data", e)

                        Result.Error("Failed to deserialize data")
                    } catch (e: IOException) {
                        Log.e(logTag, "Failed to make HTTP request due to IO error", e)

                        Result.Error(e.message ?: "Unknown error")
                    }
                    """.trimIndent()
            )

            //https://github.com/beHqo/MockStore
            KotlinCode(
                code = """
                    class ProductDetailsViewModel(
                        savedStateHandle: SavedStateHandle,
                        private val fetchProductDetailsUseCase: FetchProductDetailsUseCase
                    ) : ViewModel() {
                        val productId: Int = savedStateHandle["productId"] ?: -1

                        private val _productDetails = MutableStateFlow<Result<Product>>(Result.Loading)
                        //val productDetails = _productDetails.asStateFlow().onStart { fetchSafely() }
                            //.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), Result.Loading)
                            
                        val productDetails: StateFlow<Result<Product>> = _productDetails.asStateFlow().onStart { fetchSafely() }
                            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), Result.Loading)
    
                        private fun fetchSafely() {
                            if (productId == -1) _productDetails.update { Result.Error("Invalid product id") }
                            else viewModelScope.launch {
                                _productDetails.update { fetchProductDetailsUseCase(productId) }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/OmarDroid/NoteAppCompose
            KotlinCode(
                code = """
                    sealed class OrderType {
                        object Ascending : OrderType()
                        object Descending : OrderType()
                    }

                    sealed class NoteOrder(val orderType: OrderType) {
                        class Title(orderType: OrderType) : NoteOrder(orderType)
                        class Date(orderType: OrderType) : NoteOrder(orderType)
                        class Color(orderType: OrderType) : NoteOrder(orderType)

                        fun copy(orderType: OrderType): NoteOrder {
                            return when (this) {
                                is Title -> Title(orderType)
                                is Date -> Date(orderType)
                                is Color -> Color(orderType)
                            }
                        }
                    }

                    @Composable
                    fun OrderSection(
                        noteOrder: NoteOrder = NoteOrder.Date(OrderType.Descending),
                    ) {
                        //onClick
                        //noteOrder is NoteOrder.Title
                        //noteOrder is NoteOrder.Date

                        //OnChange
                        //noteOrder.copy(OrderType.Descending)
                     }
                    """.trimIndent()
            )

            //https://github.com/AnatoliiChub/HRAM.git
            KotlinCode(
                code = """
                    //koin validation 
                    
                    private const val MIN_NAME_LENGTH = 3
                    private const val MAX_NAME_LENGTH = 50

                    class ActivityNameValidation {
                        operator fun invoke(name: String) = when {
                            name.isBlank() -> Res.string.activity_screen_name_validation_empty
                            name.firstOrNull()?.isDigit() ?: false -> Res.string.activity_screen_name_validation_start_digit
                            name.length < MIN_NAME_LENGTH -> Res.string.activity_screen_name_validation_too_short
                            name.length > MAX_NAME_LENGTH -> Res.string.activity_screen_name_validation_too_long
                            else -> null
                        }
                    }
                    
                    @Module
                    @Configuration
                    class UtilsModule {
                        @Factory
                        fun provideActivityNameValidationUseCase() = ActivityNameValidation()
                    }
                    
                    @KoinApplication(
                        modules = [
                            UtilsModule::class,
                        ]
                    )
                    object AppModule
                    """.trimIndent()
            )

            //https://github.com/manu0396/PetFinderComposeMultiplatform.git
            KotlinCode(
                code = """
                    class LoginUseCase(
                        private val repository: AuthRepository
                    ) {
                        private fun validate(email: String, pass: String): Result<Unit> {
                            return when {
                                email.isBlank() -> Result.failure(Exception("Email cannot be empty"))
                                pass.length < 6 -> Result.failure(Exception("Password must be at least 6 characters"))
                                else -> Result.success(Unit)
                            }
                        }

                        suspend fun signIn(email: String, pass: String): Result<Unit> {
                            validate(email, pass).onFailure { return Result.failure(it) }
                            return repository.signIn(email, pass)
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/arrazyfathan/tudu-mobile
            KotlinCode(
                code = $$"""
                    //UseCase Validation
                    
                    <resources>
                        <string name="error_disk_full">Oops, it seems like your disk is full.</string>
                        <string name="error_unknown">Oops, something went wrong</string>
                        <string name="error_request_timeout">The request time out</string>
                        <string name="error_no_internet">Couldn't reach server, please check your internet connection.</string>
                        <string name="error_serialization">Couldn't parse data</string>
                        <string name="error_too_many_request">Your request is too many, please try again later.</string>
                        <string name="error_http">Something went wrong</string>
                        <string name="error_client">Something went wrong</string>
                        <string name="error_unauthorized">Unauthorized</string>
                        <string name="error_validation">Validation failed</string>
                        <string name="error_validation_password_too_short">Password is too short</string>
                        <string name="error_validation_password_blank">Password cannot be empty</string>
                        <string name="error_validation_password_confirmation_not_match">Password confirmation does not match</string>
                        <string name="error_validation_email_not_valid">Not a valid email</string>
                        <string name="error_validation_email_blank">Email cannot be empty</string>
                        <string name="error_validation_name_blank">Name cannot be empty</string>
                        <string name="error_validation_username_not_valid">Not a valid username</string>
                        <string name="error_validation_username_blank">Username cannot be empty</string>
                    </resources>
                    
                    //UiText.kt
                    sealed interface UiText {
                        data class DynamicString(
                            val value: String,
                        ) : UiText

                        class StringResourceId(
                            val id: StringResource,
                            val args: Array<Any> = arrayOf(),
                        ) : UiText

                        @Composable
                        fun asString(): String =
                            when (this) {
                                is DynamicString -> value
                                is StringResourceId -> stringResource(resource = id, formatArgs = args)
                            }
                    }
                    
                    //validations.kt
                    fun isNumber(value: String): Boolean = value.isEmpty() || Regex("^\\d+\$").matches(value)
                    fun isEmailValid(email: String): Boolean = Regex("^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,6}\$").matches(email)
                    fun isPasswordValid(password: String): Boolean = password.any { it.isDigit() } && password.any { it.isLetter() }
                    
                    //BaseUseCase.kt
                    interface BaseUseCase<In, Out> {
                        fun execute(input: In): Out
                    }

                    //ValidateEmailUseCase.kt
                    class ValidateEmailUseCase : BaseUseCase<String, ValidationResult> {
                        override fun execute(input: String): ValidationResult {
                            if (input.isBlank()) {
                                return ValidationResult(
                                    successful = false,
                                    errorMessage = UiText.StringResourceId(Res.string.error_validation_email_blank),
                                )
                            }

                            if (!isEmailValid(input)) {
                                return ValidationResult(
                                    successful = false,
                                    errorMessage = UiText.StringResourceId(Res.string.error_validation_email_not_valid),
                                )
                            }

                            return ValidationResult(
                                successful = true,
                            )
                        }
                    }
                    
                    //ValidatePasswordConfirmationUseCase.kt
                    class ValidatePasswordConfirmationUseCase : BaseUseCase<Pair<String, String>, ValidationResult> {
                        override fun execute(input: Pair<String, String>): ValidationResult {
                            val (password, confirmPassword) = input

                            if (confirmPassword.isBlank()) {
                                return ValidationResult(
                                    successful = false,
                                    errorMessage = UiText.StringResourceId(Res.string.error_validation_password_blank),
                                )
                            }

                            if (password != confirmPassword) {
                                return ValidationResult(
                                    successful = false,
                                    errorMessage = UiText.StringResourceId(Res.string.error_validation_password_confirmation_not_match),
                                )
                            }

                            return ValidationResult(successful = true)
                        }
                    }
                    
                    data class RegisterState(
                        val username: String = "",
                        val usernameError: UiText? = null,
                        val name: String = "",
                        val nameError: UiText? = null,
                        val email: String = "",
                        val emailError: UiText? = null,
                        val password: String = "",
                        val passwordError: UiText? = null,
                        val passwordConfirmation: String = "",
                        val passwordConfirmationError: UiText? = null,
                        val isLoading: Boolean = false,
                        val errorMessage: UiText? = null,
                        val showToast: Boolean = false,
                    )
                    
                    sealed interface RegisterAction {
                        data object OnRegister : RegisterAction

                        data class OnUsernameChange(
                            val username: String,
                        ) : RegisterAction

                        data class OnNameChange(
                            val name: String,
                        ) : RegisterAction

                        data class OnEmailChange(
                            val email: String,
                        ) : RegisterAction

                        data class OnPasswordChange(
                            val password: String,
                        ) : RegisterAction

                        data class OnPasswordConfirmationChange(
                            val passwordConfirmation: String,
                        ) : RegisterAction
                    }

                    class RegisterViewModel(
                        private val registerUseCase: RegisterUseCase,
                    ) : ViewModel() {
                        private val validateUsername = ValidateUsernameUseCase()
                        private val validateEmail = ValidateEmailUseCase()
                        private val validatePassword = ValidatePasswordUseCase()
                        private val validatePasswordConfirmation = ValidatePasswordConfirmationUseCase()
                        private val validateName = ValidateNameUseCase()
                        
                        private val _state = MutableStateFlow(RegisterState())
                        val state = _state.asStateFlow()
                        
                         fun onAction(action: RegisterAction) {
                            when (action) {
                                is RegisterAction.OnPasswordConfirmationChange -> {
                                    _state.update { it.copy(passwordConfirmation = action.passwordConfirmation) }
                                    validatePasswordConfirmation()
                                }
                                
                                is RegisterAction.OnEmailChange -> {
                                    _state.update { it.copy(email = action.email) }
                                    validateEmail()
                                }
                            }
                         }
                        
                        private fun validatePasswordConfirmation(): Boolean {
                            val passwordConfirmationResult =
                                validatePasswordConfirmation.execute(
                                    Pair(
                                            _state.value.password,
                                            _state.value.passwordConfirmation,
                                    ),
                                )
                            _state.update { it.copy(passwordConfirmationError = passwordConfirmationResult.errorMessage) }
                            return passwordConfirmationResult.successful
                        }
                        
                        private fun validateEmail(): Boolean {
                            val emailResult = validateEmail.execute(_state.value.email)
                            _state.update { it.copy(emailError = emailResult.errorMessage) }
                            return emailResult.successful
                        }
                    }
                    """.trimIndent()
            )

            //Coroutines Job Validation
            //https://github.com/Moscow-Squad/CineVerse
            KotlinCode(
                code = """
                    //Coroutines Job Using Validation
                    
                    abstract class BaseViewModel<T, E>(
                        initialState: T
                    ) : ViewModel() {

                        private val _uiState = MutableStateFlow(initialState)
                        val uiState: StateFlow<T> = _uiState.asStateFlow()

                        private val _uiEffect = Channel<E>()
                        val uiEffect = _uiEffect.receiveAsFlow()

                        protected fun updateState(transform: (T) -> T) {
                            _uiState.update { transform(it) }
                        }

                        protected fun sendEvent(event: E) {
                            viewModelScope.launch {
                                _uiEffect.send(event)
                            }
                        }

                        protected fun <R> launchWithResult(
                            action: suspend () -> R,
                            onSuccess: (R) -> Unit,
                            onError: (Int) -> Unit,
                            onStart: () -> Unit = {},
                            onFinally: () -> Unit = {}
                        ) {
                            viewModelScope.launch(Dispatchers.IO) {
                                onStart()
                                runCatching { action() }
                                    .onSuccess(onSuccess)
                                    .onFailure { e ->
                                        val msg = (e as Exception).handleException()
                                        onError(msg)
                                    }
                                onFinally()
                            }
                        }

                        protected fun launchAndForget(
                            action: suspend () -> Unit,
                            onSuccess: () -> Unit = {},
                            onError: (Int) -> Unit,
                            onStart: () -> Unit = {},
                            onFinally: () -> Unit = {}
                        ) = viewModelScope.launch(Dispatchers.IO) {
                            onStart()
                            runCatching { action() }
                                .onSuccess { onSuccess() }
                                .onFailure{ e ->
                                    val msg = (e as Exception).handleException()
                                    onError(msg)
                                }
                            onFinally()
                        }

                        protected fun <R> launchWithFlow(
                            flowAction: suspend () -> Flow<R>,
                            onSuccess: (R) -> Unit,
                            onError: (Int) -> Unit,
                            onStart: () -> Unit = {},
                            onFinally: () -> Unit = {}
                        ) {
                            viewModelScope.launch(Dispatchers.IO) {
                                onStart()
                                try {
                                    flowAction().collect { result ->
                                        onSuccess(result)
                                    }
                                } catch (e: Exception) {
                                    onError(e.handleException())
                                } finally {
                                    onFinally()
                                }
                            }
                        }
                    }
                    
                    sealed class LoginScreenEvents {
                        data class ShowError(val message: StringValue): LoginScreenEvents()
                        data object NavigateTo: LoginScreenEvents()
                    }
                    
                    data class LoginScreenState(
                        val username: String = "",
                        val password: String = "",
                        val usernameError: StringValue? = null,
                        val passwordError: StringValue? = null,
                        val loginError: StringValue? = null,
                        val isLoading: Boolean = false,
                        val showBrowserBottomSheet: Boolean = false,
                        val urlWebView: String = "",
                        val showWebView: Boolean = false,
                        val isJoinAsGuest: Boolean = false,
                        val isForgotPassword: Boolean = false
                    )
                    
                    interface LoginInteractionListener {
                        fun onUsernameValueChanged(username: String)
                        fun onPasswordValueChanged(password: String)
                        fun onClickLogin()
                        fun onClickJoinAsGuest()
                        fun onClickCreateNewAccount()
                        fun onDismissOrCancelBrowserBottomSheet()
                        fun onClickGoToWebsite()
                        fun onClickForgetPassword()
                        fun onExitWebViewBrowser()
                    }
                    
                    @HiltViewModel
                    class LoginViewModel @Inject constructor(
                    ) : BaseViewModel<LoginScreenState, LoginScreenEvents>(LoginScreenState()),
                        LoginInteractionListener {

                        private var usernameValidationJob: Job? = null
                        private var passwordValidationJob: Job? = null

                        override fun onUsernameValueChanged(username: String) {
                            if (username.length <= 33) {
                                updateState {
                                    it.copy(
                                        username = username,
                                        loginError = null
                                    )
                                }
                            }
                            usernameValidationJob?.cancel()

                            usernameValidationJob = validateInput(
                                input = username,
                                isPassword = false,
                                onResult = { error ->
                                    updateState { it.copy(usernameError = error) }
                                }
                            )
                        }
                        
                        private fun validateInput(
                            input: String,
                            isPassword: Boolean,
                            delayMillis: Long = 100,
                            onResult: (StringValue?) -> Unit
                        ): Job {
                            return viewModelScope.launch {
                                delay(delayMillis)

                                val trimmed = input.trim()

                                val error = when {
                                    input.isEmpty() -> null

                                    !isPassword && !trimmed.all { it.isLetterOrDigit() || it == '_' } -> {
                                        StringValue.StringResource(R.string.username_invalid_chars)
                                    }

                                    !isPassword && input != trimmed -> {
                                        StringValue.StringResource(R.string.no_leading_trailing_spaces)
                                    }

                                    !isPassword && trimmed.length !in 4..32 -> {
                                        StringValue.StringResource(R.string.username_length_invalid)
                                    }

                                    isPassword && input.length !in 4..100 -> {
                                        StringValue.StringResource(R.string.password_can_only_4_100_characters)
                                    }

                                    else -> null
                                }

                                onResult(error)
                            }
                        }
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "Throws Exception",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/OmarDroid/NoteAppCompose
            KotlinCode(
                code = """
                    @Entity
                    data class Note(
                    @PrimaryKey
                    val id : Int? = null,
                    val title : String,
                    val content : String,
                    val timeStamp : Long,
                    val color : Int
                    ) {
                        companion object {
                            val noteColors = listOf(RedOrange, RedPink, BabyBlue, Violet, LightGreen)
                        }
                    }

                    val RedOrange = Color(0xffffab91)
                    val RedPink = Color(0xfff48fb1)
                    val BabyBlue = Color(0xff81deea)
                    val Violet = Color(0xffcf94da)
                    val LightGreen = Color(0xffe7ed9b)

                    class AddNoteUseCase(private val repository: NoteRepository) {
                        @Throws(InvalidNoteException::class)
                        suspend operator fun invoke(note: Note) {
                            if (note.title.isBlank()) {
                                throw InvalidNoteException("Title of the note can't be empty")
                            }
                            if (note.content.isBlank()) {
                                throw InvalidNoteException("The content of the note can't be empty")
                            }
                            repository.insertNote(note)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Mapper Plugin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Automatic Mapper Generation: Generate mapper functions with just a few clicks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Easy-to-Use UI: A user-friendly interface for selecting source and target objects and fine-tuning the mappings.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Koans",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Koans in JetBrains Academy Plugin for IntelliJ Idea and Android Studio",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Kotlin AutoMapper (Kotlin 2.3.0, KSP 2.3.6)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/jacksever/kotlin-automapper
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Effortless, type-safe object-to-object mapping in Kotlin. Tired of writing boilerplate code to convert one object to another? This library does it for you at compile time, with full support for Kotlin Multiplatform. Kotlin AutoMapper uses KSP (Kotlin Symbol Processing) to generate extension functions that automatically map your data, enum, and sealed classes. No reflection, no runtime magic - just pure, fast, and safe generated code for all your targets.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "komm",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Kotlin Object Multiplatform Mapper provides you a possibility to generate (via KSP) extension function to map one object to another.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Auto Mapping",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    implementation("dev.krud:shapeshift:0.8.0") //DSL
                    """.trimIndent()
            )

            SpanText(
                text = "Kraft",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                   plugins {
                       kotlin("multiplatform") // or kotlin("jvm") / kotlin("android")
                       id("com.google.devtools.ksp") version "<ksp-version>"
                       id("com.blu3berry.kraft") version "<version>"
                   }
                   
                   kraft {
                       side("dto")    { packagePattern = "com.example.dto.**" }     // emits .toDto() aliases
                       side("domain") { packagePattern = "com.example.domain.**" }  // emits .toDomain() aliases
                   }
                    """.trimIndent()
            )

            SpanText(
                text = "Where should a discount rule live: Entity or UseCase?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If cart value > ₹1000, give 10% discount. Put it in the UseCase to keep the Entity clean. It depends on rule stability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Core pricing rule (e.g., tax rate, permanent pricing logic) → Entity",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Context-dependent rule (festival, campaign, coupon, A/B test) → UseCase",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Why do dependencies always point inward in Clean Architecture?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Because business rules must not depend on details — details must depend on business rules.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What the system does must not depend on how it is delivered.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI → UseCase → Entity",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Data → UseCase → Entity",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI depends on Domain",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Data depends on Domain",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Domain depends on nothing",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "How many mappers are required in a Clean Architecture app?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "One mapper per layer. Whenever data crosses a layer boundary, a mapper may be required.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "DTO → Domain",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Entity → Domain",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Domain → Entity",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Domain → UI Model",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Avoid In-Memory Global State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class SessionManager {
                        private val _accessToken = MutableStateFlow<String?>(null)
                        val accessToken = _accessToken.asStateFlow()	
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This code is especially problematic in the Android environment because of process death. This is where the Android system kills your app’s process when it needs to free up some memory for other tasks. The catch is that the Android system automatically tries to restore your app’s backstack when the user returns the app from the background. However, the memory was still cleared, so our session token was also cleared and reset to the default value. This can cause your user to be on a screen that requires a valid token, but now the app no longer has the token – not an ideal state for your app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When we have a global state like a session token, the most secure place to store it is persistent storage, such as SharedPreferences. Doing so lets you observe or access the state from there, protecting it from process death and avoiding hard-to-find bugs.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Backend changes completely (Firebase → OAuth). Which layers change?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Data layer and maybe Repository.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Can you discuss your approach to collaborating with cross-functional teams in an Agile environment?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Agile, collaboration is key to delivering quality software quickly. I actively participate in:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Daily Stand-ups: To report progress, blockers, and plan next steps.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Sprint Planning: Helping estimate tasks and define user stories.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Sprint Reviews/Demos: Presenting features and gathering feedback.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Retrospectives: Discussing improvements in processes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "I work closely with Product Owners to understand requirements and designers to implement UI/UX guidelines. Coordinating with QA ensures that test cases cover edge cases and bugs are promptly fixed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, during a sprint, when QA found a UI bug, I quickly fixed it, updated test cases, and communicated the changes in the next stand-up to keep the team aligned.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "STAR Method Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The STAR method helps you create an easy-to-follow story with a clear conflict and resolution. Here is what each part of the technique means.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Situation - Set the stage for the story by sharing context around the situation or challenge you faced.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Task - Responsibility or role in the situation or challenge resolving it.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Action -  Steps you took. Explain how you handled the situation or overcame the challenge. If the action was carried out by a team, focus on your efforts.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Result - The measurable impact.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "MVVM",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "MVVM stands for Model-View-ViewModel. It's an architectural pattern that helps organize your Android code in a clean, maintainable way.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Model = The kitchen (where food is prepared/data is managed)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "View = The dining area (what customers see)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel = The waiter (connects kitchen and dining area",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Handling Error Using runCatching (no use try catch)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/EfeObus/VwaTek-Apply.git
            KotlinCode(
                code = $$"""
                    class PrivacyApiClient(
                        private val httpClient: HttpClient,
                        private val getAuthToken: () -> String?
                    ) {
                        /**
                         * Get all consent statuses for current user
                         */
                        suspend fun getAllConsents(): Result<AllConsentsResult> {
                            return runCatching {
                                val token = getAuthToken() ?: throw IllegalStateException("No auth token available")

                                val response: ApiAllConsentsResponse =
                                    httpClient.get("${ApiConfig.privacyApiUrl}/consent") {
                                        header(HttpHeaders.Authorization, "Bearer $token")
                                    }.body()

                                AllConsentsResult(
                                    consents = response.consents.map { it.toDomain() },
                                    policyVersion = response.policyVersion,
                                    lastUpdated = response.lastUpdated
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Single Questions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Which layer typically instantiates and calls the Repository?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "ViewModel - ViewModels manage Ul-related data and request it from repositories.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the \"Single Source of Truth\"? ",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A specific data source (often the DB) that acts as the authoritative source for the UI - Ul always observes this source; other sources (Network) only update it. Repositories make sure your app has one consistent place to fetch data.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why should a Repository hide its data sources?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "To allow switching data sources (e.g., mock vs real) without affecting Ul code.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the role of a Repository?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "A Repository is an abstraction in the Domain layer that defines how data operations are accessed and is implemented in the Data layer.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the \"Repository\" pattern?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = " A mediator between data sources (Network, DB) and the rest of the app - Centralizes data access and provides a clean API to ViewModels.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Should a Repository know about the View (Activity/Fragment)?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "No - Repositories should be Ul-agnostic to be reusable and testable.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How is data passed between layers?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Using data classes and mappers to transform models (e.g., from DTO → Domain → UIModel).",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "What is the responsibility of ViewModel?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "ViewModel sits in the Presentation layer and communicates with UseCases. It exposes state using StateFlow Or LiveData.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Why we separate Repository and its implementation.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Repository (interface) = contract",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "RepositoryImpl (implementation) = details (API, DB, cache, etc.)",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }

                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "We separate repository and its implementation to achieve abstraction, testability, flexibility, and maintain clean architecture by decoupling business logic from data sources. Swap implementations easily.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How ViewModel interacts with the data layer.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }


                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Should composables perform network calls directly?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Generally no. Network work belongs in ViewModels/use cases/data layers.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "Android implementation flow.",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "How data and logic move through your app from UI → data source → back to UI, using clean architecture + modern tools. MVI Explain",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                   
                            )
                        }
                    }
                }

                Li(Modifier.padding(top = 1.em).toAttrs()) {
                    SpanText(
                        text = "How does Clean Architecture work in a multi-module Android project?",
                        modifier = Modifier.fontSize(FontSize.Large)
                            .fontWeight(FontWeight.Bold)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Clean Architecture splits code into layers: Presentation (Compose/ViewModel), Domain (UseCases, Entities — pure Kotlin, no Android deps), and Data (Repositories, APIs, DB).",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "In a multi-module project each layer becomes its own Gradle module. The Domain module is the most important — it has zero Android dependencies, making it fully unit-testable and reusable in KMP. The Data module depends on Domain, and Presentation depends on Domain only, never Data directly. This keeps dependencies pointing inward.",
                                modifier = Modifier.fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }
        }
    }
}

//https://medium.com/@ramadan123sayed/kmp-part-2-sharing-data-models-business-logic-and-validation-between-android-and-ios-domain-167c2f9fa738
//https://github.com/music-assistant/mobile-app.git (Result Handle kotlinx.serialization JsonElement)
//https://github.com/xephosbot/AnilibriaRefresh (KSerializer)
//https://github.com/Luwaphemi/DataTruth.git (KSerializer)
//https://github.com/mikaere66/DiscoverMultiplatform (KSerializer)
//https://github.com/joost-klitsie/StateMachine/ (State Machine)
//https://github.com/YouXiang8727/StreamletMultiplatform (MVI)
//https://github.com/philipplackner/ContactsComposeMultiplatform/tree/final (Server-Driven UI)
//https://medium.com/@cosmin.mihu/clean-architecture-with-kotlin-97e246d0361b
//FlowMVI
//https://kabi20.medium.com/from-spaghetti-to-solid-decoupling-your-android-data-layer-for-cleaner-code-in-kotlin-0986fc82c270
//https://github.com/alviandf/MyMVISample
//https://github.com/skymansandy/kurlclient.git (MVI BaseViewModel good)
//https://mzeus.medium.com/why-mvi-architecture-is-essential-in-kotlin-multiplatform-4910be2a29e9
//https://github.com/stellelibs?tab=repositories (MVI Library)
//https://medium.com/@himanshugaur684/build-a-reusable-validation-framework-in-kotlin-using-solid-principles-4409d6bc1d5f (Validation)

//https://abifarhan.medium.com/complete-system-design-instagram-feed-android-f4953c1ba2fc
//https://medium.com/@avula.koti.realpage/15-android-architecture-questions-every-senior-developer-must-answer-in-2026-e713b8903d10
//https://medium.com/@avula.koti.realpage/15-android-architecture-questions-every-senior-developer-must-answer-in-2026-e713b8903d10